/**
 * Unify FHIR object model constants — extension URLs, coding systems, tag systems.
 *
 * Single source of truth for medplum-ui AND medplum-server — import via
 * `@unify/fhir-model/constants`. If you add a new extension URL or coding
 * system, add it here — not inline in app, seed, or bot files.
 */

// ═══════════════════════════════════════════════════════════════════════
//  Profile / role extensions
// ═══════════════════════════════════════════════════════════════════════

export const EXT_URLS = {
  role: 'https://unify.health/fhir/role',
  uiPermissions: 'https://unify.health/fhir/ui-permissions',
  uiPermissionOverrides: 'https://unify.health/fhir/ui-permission-overrides',
  portalSubjectPatient: 'https://unify.health/fhir/portal-subject-patient',
  portalRelatedPatient: 'https://unify.health/fhir/portal-related-patient',
  pronouns: 'https://unify.health/fhir/pronouns',
  supervisingPractitioner: 'https://unify.health/fhir/supervising-practitioner',
  /** Reverse linkage on supervisor Practitioner — kept in sync with supervisee canonical extension. */
  superviseePractitioner: 'https://unify.health/fhir/supervisee-practitioner',
  /** Date (YYYY-MM-DD) when the current supervising-practitioner assignment began (on supervisee Practitioner). */
  supervisionStartedAt: 'https://unify.health/fhir/supervision-started-at',
  /**
   * Whether this Practitioner's clinical documentation requires supervisor cosignature.
   * Independent of having a supervising-practitioner edge — do not infer cosign from the edge alone.
   */
  requiresCosignature: 'https://unify.health/fhir/requires-cosignature',
  /**
   * Human actor who last assigned/changed PractitionerRole.code[] (valueReference → Practitioner).
   * Needed because bot writes stamp `meta.author` as the Bot, not the triggering Practitioner.
   */
  roleAssignedBy: 'https://unify.health/fhir/role-assigned-by',
} as const;

/** Canonical supervisee → supervisor extension URL. */
export const SUPERVISING_PRACTITIONER_EXTENSION_URL = EXT_URLS.supervisingPractitioner;
/** Reverse supervisor → supervisee extension URL (display/editing; canonical source is supervising-practitioner on supervisee). */
export const SUPERVISEE_PRACTITIONER_EXTENSION_URL = EXT_URLS.superviseePractitioner;
/** Explicit cosign-required flag on Practitioner (valueBoolean). */
export const REQUIRES_COSIGNATURE_EXTENSION_URL = EXT_URLS.requiresCosignature;

export const ADMIN_EXT_URLS = {
  organization: 'https://unify.health/fhir/organization',
  practiceOwner: 'https://unify.health/fhir/practice-owner',
} as const;

export const INTAKE_EXT_URLS = {
  credentials: 'https://unify.health/fhir/credentials',
  shortBio: 'https://unify.health/fhir/short-bio',
  longBio: 'https://unify.health/fhir/long-bio',
  headshotUrl: 'https://unify.health/fhir/headshot-url',
  sessionFormats: 'https://unify.health/fhir/session-formats',
  acceptsInsurance: 'https://unify.health/fhir/accepts-insurance',
  takingNewClients: 'https://unify.health/fhir/taking-new-clients',
  intakeProvider: 'https://unify.health/fhir/intake-provider',
} as const;

export const CAQH_EXT_URLS = {
  id: 'https://unify.health/fhir/caqh-id',
  username: 'https://unify.health/fhir/caqh-username',
  password: 'https://unify.health/fhir/caqh-password',
  attestationDate: 'https://unify.health/fhir/caqh-attestation-date',
} as const;

// ═══════════════════════════════════════════════════════════════════════
//  Clinical / episode extensions
// ═══════════════════════════════════════════════════════════════════════

// Used on Appointment, QuestionnaireResponse, and Task to link to an EpisodeOfCare for context.
// Note: ServiceRequest uses SERVICE_REQUEST_EOC_EXT (StructureDefinition path) — intentionally different.
export const EPISODE_OF_CARE_CONTEXT_EXT = 'https://unify.health/fhir/episode-of-care-context';
/**
 * HL7 workflow episode link — preferred on CarePlan (and other resources that
 * support it). Unify’s EPISODE_OF_CARE_CONTEXT_EXT remains on QR/Appointment/Task/etc.
 */
export const WORKFLOW_EPISODE_OF_CARE_EXT =
  'http://hl7.org/fhir/StructureDefinition/workflow-episodeOfCare';
/** Links a portal proposed reschedule appointment to the cancelled original. */
export const PORTAL_RESCHEDULE_ORIGINAL_APPOINTMENT_EXT =
  'https://unify.health/fhir/StructureDefinition/portal-reschedule-original-appointment';
export const GUARANTOR_EXT = 'https://unify.health/fhir/guarantor';
/** Unify lifecycle status. On QuestionnaireResponse it is the authoritative UI status; on EpisodeOfCare it is the hold reason when status is onhold. */
export const DETAILED_STATUS_EXT = 'https://unify.health/fhir/detailed-status';
export const IOP_PARENT_ENCOUNTER_EXT = 'https://unify.health/fhir/iop-parent-encounter';
export const ATTENDANCE_STATUS_EXT = 'https://unify.health/fhir/attendance-status';
export const ONBOARDING_ATTESTATION_EXT = 'https://unify.health/fhir/onboarding-attestation';
export const ONBOARDING_ATTESTATION_DATE_EXT = 'https://unify.health/fhir/onboarding-attestation-date';
export const PARTICIPANTS_EXT = 'https://unify.health/fhir/StructureDefinition/participants';
/** RelatedPerson extension: linked Patient login profile when the participant is also a therapy client. */
export const PARTICIPANT_PROFILE_EXT = 'https://unify.health/fhir/participant-profile';
export const ENCOUNTER_REASON_CANCELLED_EXT = 'https://unify.health/fhir/StructureDefinition/encounter-reason-cancelled';
/** Marks assessment-path Encounters (excluded from appointment ChargeItem backfill). */
export const ASSESSMENT_TASK_EXTENSION = 'https://unify.health/fhir/StructureDefinition/assessment-task';

// ═══════════════════════════════════════════════════════════════════════
//  Appointment extensions
// ═══════════════════════════════════════════════════════════════════════

export const VIDEO_MEETING_URL_EXT = 'https://unify.health/fhir/StructureDefinition/video-meeting-url';
/**
 * Chime session state: four flat Appointment extensions, staff-only because client policies hide `extension` (design Decisions 34–35).
 * The meeting id is only `Appointment.identifier` (`CHIME_MEETING_IDENTIFIER_SYSTEM`). Join tokens are never stored.
 */
export const CHIME_SESSION_EXTS = {
  /** `valueString`: AWS requires it to start a recording. */
  meetingArn: 'https://unify.health/fhir/chime-session/meeting-arn',
  /** `valueCode` `created` | `active` | `ended`. */
  status: 'https://unify.health/fhir/chime-session/status',
  /** `valueString`: the current or last capture pipeline; stops it, matches Chime's pipeline events, ignores replaced ones. */
  mediaPipelineId: 'https://unify.health/fhir/chime-session/media-pipeline-id',
  /** Retired (design Decision 39): no longer read; `setChimeSession` removes it. */
  recordingStatus: 'https://unify.health/fhir/chime-session/recording-status',
} as const;
/** Task.code (`TASK_TYPE_SYSTEM`) of a Chime waiting-room entry: one Task per person joining (design Decision 34). */
export const CHIME_WAITING_ROOM_TASK_CODE = 'chime-waiting-room';
/** Task.identifier system of a link guest's opaque handle (32 hex chars, a hash of their join key). */
export const CHIME_GUEST_HANDLE_IDENTIFIER_SYSTEM = 'https://unify.health/chime-guest-handle';
/**
 * Appointment extension — signed link that lets anyone outside the system reach the
 * waiting room. `VIDEO_MEETING_URL_EXT` holds the patient-bound link used by reminders.
 */
export const TELEHEALTH_GUEST_LINK_EXT =
  'https://unify.health/fhir/StructureDefinition/telehealth-guest-link';
/** Identifier.system for Appointment lookup by Chime meeting id. */
export const CHIME_MEETING_IDENTIFIER_SYSTEM = 'https://unify.health/chime-meeting-id';
/** Identifier.system for the Appointment's random guest-link code (`/join/<code>`). */
export const GUEST_LINK_CODE_IDENTIFIER_SYSTEM = 'https://unify.health/guest-link-code';
/** Appointment.participant extension (`valueString`): the participant's personal-link code (`?p=<code>`). */
export const PARTICIPANT_LINK_CODE_EXT = 'https://unify.health/fhir/StructureDefinition/participant-link-code';
/**
 * Private-record extension: the transcript pointer, one value per Appointment with `url` (the fixed
 * `s3://<bucket>/transcripts/<appointmentId>.txt`), `sha256`, `size` and `written-at`. Holds no text.
 */
export const SESSION_TRANSCRIPT_EXT = 'https://unify.health/fhir/StructureDefinition/session-transcript';
export const SESSION_TRANSCRIPT_SUB = {
  url: 'https://unify.health/fhir/session-transcript/url',
  sha256: 'https://unify.health/fhir/session-transcript/sha256',
  size: 'https://unify.health/fhir/session-transcript/size',
  writtenAt: 'https://unify.health/fhir/session-transcript/written-at',
} as const;
/** EventBridge `source` and `detail-type` of the transcription Lambda's result (design Decision 34). */
export const SESSION_TRANSCRIPT_RESULT_SOURCE = 'unify.session-transcript';
export const SESSION_TRANSCRIPT_RESULT_DETAIL_TYPE = 'Session Transcript Result';
export const RECURRING_PATTERN_EXT = 'https://unify.health/fhir/StructureDefinition/recurring-pattern';
export const ATTENDANCE_SOURCE_EXT = 'https://unify.health/fhir/StructureDefinition/attendance-source';
/**
 * Written by `appointment-tools` `seal-appointment` after `sign-appointment-note` signs the
 * note (two writes; the lock may lag or be absent after a failed seal). Carries sub-extensions
 * `at` (instant), `note` (QuestionnaireResponse reference) and `version` (sealed `meta.versionId`)
 * with no value on the parent (FHIR `ext-1`); legacy locks carry only a parent `valueInstant`.
 * An in-flight attendance PUT is protected by If-Match and by `save-attendance` re-checking
 * `appointmentNoteLocksWrite` on a 412, not by a same-transaction bump.
 */
export const ATTENDANCE_LOCK_GENERATION_EXT = 'https://unify.health/fhir/attendance-lock-generation';
/**
 * Provider scratch notes from the session clinical sidebar (guide AI generation; not in the signed note).
 * The URL keeps its Zoom-era name because existing Appointments carry it.
 */
export const SESSION_SIDEBAR_NOTES_EXT = 'https://unify.health/fhir/zoom-sidebar-session-notes';
/**
 * Appointment `valueCode` for the Modulate + Gemini post-session pipeline.
 * Do not overload `SESSION_TRANSCRIPT_READY_AT_EXT` for in-flight states.
 */
export const SESSION_NOTE_PIPELINE_STATUS_EXT =
  'https://unify.health/fhir/session-note-pipeline-status';
export const SESSION_NOTE_PIPELINE_STATUS_CODES = [
  'queued',
  'transcribing',
  'generating',
  'failed',
  'opted_out',
] as const;
export type SessionNotePipelineStatus = (typeof SESSION_NOTE_PIPELINE_STATUS_CODES)[number];
/** Attachment extension: the Chime media pipeline (capture segment) a transcript part came from. */
export const SESSION_TRANSCRIPT_SEGMENT_EXT = 'https://unify.health/fhir/session-transcript-segment';
/** Appointment extension (repeating, nested): a capture segment the Lambda is transcribing now. */
export const SESSION_TRANSCRIPT_SEGMENT_CLAIM_EXT = 'https://unify.health/fhir/session-transcript-segment-claim';
/** DocumentReference extension (repeating): replaced or unreferenced transcript Binary ids for the cleanup bot to delete. */
export const SESSION_TRANSCRIPT_PENDING_BINARY_DELETE_EXT =
  'https://unify.health/fhir/session-transcript-pending-binary-delete';
/**
 * Appointment extension: token identifying the in-flight note-generation run. A second,
 * concurrent ingest (a different capture segment) can start its own generation run while an
 * earlier one is still executing; whichever finishes last must not report `ready` on behalf of a
 * run it does not own, so each run only stamps `ready` when its own token is still current.
 */
export const SESSION_TRANSCRIPT_GENERATION_RUN_EXT = 'https://unify.health/fhir/session-transcript-generation-run';
/** `Appointment.identifier` so workers can search `queued` jobs without a custom SearchParameter. */
export const SESSION_TRANSCRIPT_PIPELINE_IDENTIFIER_SYSTEM =
  'https://unify.health/session-transcript-pipeline';
export const SESSION_TRANSCRIPT_PIPELINE_QUEUED_VALUE = 'queued';
/** DocumentReference.type for 7-day session transcripts stored in Medplum. */
export const SESSION_TRANSCRIPT_DOCUMENT_TYPE_CODE = 'session-transcript';
/**
 * Client-attested physical location for a telehealth session: on each `Encounter.location` entry (the confirmer and
 * attestation sub-extensions; the address is the contained Location). With no Encounter, and older data: on the
 * Appointment, address included.
 */
export const CLIENT_SESSION_LOCATION_CONFIRMATION_EXT =
  'https://unify.health/fhir/StructureDefinition/client-session-location-confirmation';
export const CLIENT_LOCATION_CONFIRMATION_SUB_EXT = {
  /** street, city, state, postal-code, confirmed-at: the Appointment form only. */
  street: 'https://unify.health/fhir/client-location/street',
  city: 'https://unify.health/fhir/client-location/city',
  state: 'https://unify.health/fhir/client-location/state',
  postalCode: 'https://unify.health/fhir/client-location/postal-code',
  confirmedAt: 'https://unify.health/fhir/client-location/confirmed-at',
  source: 'https://unify.health/fhir/client-location/source',
  attestationVersion: 'https://unify.health/fhir/client-location/attestation-version',
  attestationText: 'https://unify.health/fhir/client-location/attestation-text',
  /** Reference to the confirming client participant: `Patient/<id>` or `RelatedPerson/<id>`. */
  confirmedByPatient: 'https://unify.health/fhir/client-location/confirmed-by-patient',
  wasEditedAfterPrefill: 'https://unify.health/fhir/client-location/was-edited-after-prefill',
  ipDerivedCity: 'https://unify.health/fhir/client-location/ip-derived-city',
  ipDerivedState: 'https://unify.health/fhir/client-location/ip-derived-state',
  stateMismatch: 'https://unify.health/fhir/client-location/state-mismatch',
  licensureBlocking: 'https://unify.health/fhir/client-location/licensure-blocking',
  notAtUsualAddress: 'https://unify.health/fhir/client-location/not-at-usual-address',
  practitionerId: 'https://unify.health/fhir/client-location/practitioner-id',
} as const;

/** Repeating extension on Schedule — recurring weekly client-bookable windows (PRD_PROVIDER_AVAILABILITY_FHIR_MAPPING). */
export const WEEKLY_AVAILABILITY_EXT =
  'https://unify.health/fhir/StructureDefinition/weekly-availability';

// ═══════════════════════════════════════════════════════════════════════
//  Reservation extensions (Appointment + ServiceRequest)
// ═══════════════════════════════════════════════════════════════════════

export const RESERVATION_SESSION_EXT = 'https://unify.health/fhir/reservation-session';
export const RESERVATION_EXPIRES_AT_EXT = 'https://unify.health/fhir/reservation-expires-at';

// ═══════════════════════════════════════════════════════════════════════
//  Referral request Task (PRD_REFERRAL_REQUESTS_FHIR_MAPPING)
// ═══════════════════════════════════════════════════════════════════════

/** Task.input.type coding system. */
export const TASK_INPUT_SYSTEM = 'https://unify.health/fhir/CodeSystem/task-input';
/**
 * `Task.input.type` code (TASK_INPUT_SYSTEM) on client form and assessment Tasks: `valueReference` is the
 * provider- or system-saved `pending-client` QuestionnaireResponse the client fills in.
 */
export const ASSIGNED_RESPONSE_INPUT_CODE = 'assigned-response';
/** Task.output.type coding system. */
export const TASK_OUTPUT_SYSTEM = 'https://unify.health/fhir/CodeSystem/task-output';
/** Disposition codes on Task.output.extension[assignment-disposition]. */
export const ASSIGNMENT_DISPOSITION_CS_SYSTEM = 'https://unify.health/fhir/CodeSystem/assignment-disposition';
export const ASSIGNMENT_DISPOSITION_EXT = 'https://unify.health/fhir/StructureDefinition/assignment-disposition';
/** Instant an `assigned-episode` output was written (`Task.output.extension`). */
export const ASSIGNMENT_ASSIGNED_AT_EXT = 'https://unify.health/fhir/StructureDefinition/assigned-at';
/** Task-level extension: provider cancel is queued until reserved outputs clear. */
export const CANCELLATION_QUEUED_EXT = 'https://unify.health/fhir/StructureDefinition/cancellation-queued';
/** `Task.code` coding (TASK_TYPE_SYSTEM) for provider capacity requests. */
export const REFERRAL_REQUEST_TASK_CODE = 'referral-request';

// ═══════════════════════════════════════════════════════════════════════
//  ServiceRequest preference / payment extensions
// ═══════════════════════════════════════════════════════════════════════

export const PAYMENT_TYPE_EXT = 'https://unify.health/fhir/StructureDefinition/payment-type';
export const SESSION_FORMAT_PREF_EXT = 'https://unify.health/fhir/session-format-preference';
export const AVAILABILITY_PREF_EXT = 'https://unify.health/fhir/availability-preference';
export const AREAS_OF_FOCUS_EXT = 'https://unify.health/fhir/areas-of-focus';
export const PROVIDER_PREFERENCES_EXT = 'https://unify.health/fhir/provider-preferences';
export const THERAPY_APPROACHES_EXT = 'https://unify.health/fhir/therapy-approaches';
export const PREFERRED_LOCATIONS_EXT = 'https://unify.health/fhir/preferred-locations';

// ═══════════════════════════════════════════════════════════════════════
//  App-wide staff presence ("who is working on / viewing which row")
// ═══════════════════════════════════════════════════════════════════════

/**
 * Ephemeral, per-browser-tab presence is stored as a PHI-free `Basic` resource.
 * `Basic` is deliberately chosen because no patient/guarantor portal AccessPolicy
 * grants `Basic`, so presence is excluded from portal users by construction.
 * Constant URLs retain historical `tracker-presence` naming; semantics are shared
 * across the Service Request Tracker and billing screens.
 *
 * - `code` = `PRESENCE_CODE_SYSTEM|PRESENCE_CODE` — broad selector for the WebSocket
 *   subscription, the seed search, and the staff access-policy entry.
 * - `identifier` = `PRESENCE_SESSION_IDENTIFIER_SYSTEM|<practitionerId>:<tabId>` —
 *   unique key for the conditional upsert (one resource per browser tab).
 * - `subject` = `ServiceRequest/{rowId}` | `Claim/{rowId}` | `Encounter/{rowId}`;
 *   `author` = the staff Practitioner.
 * - `PRESENCE_DATA_EXT` valueString holds JSON
 *   `{ scope, rowId, name, initials, photoUrl?, expiresAt, active }` where `scope` is
 *   `tracker` | `billing-claims` | `billing-encounters` (staff identity + opaque id only).
 */
export const PRESENCE_CODE_SYSTEM = 'https://unify.health/fhir/CodeSystem/tracker-presence';
export const PRESENCE_CODE = 'presence';
export const PRESENCE_SESSION_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/tracker-presence-session';
export const PRESENCE_DATA_EXT = 'https://unify.health/fhir/tracker-presence-data';

// ═══════════════════════════════════════════════════════════════════════
//  User signature preference (Basic — "save signature for future")
// ═══════════════════════════════════════════════════════════════════════

/**
 * PHI-free reusable e-signature preference stored as a `Basic` resource.
 * Written by `useUserSignature` when the sign modal's "save for future" option is on
 * (checked by default). Scoped to a Practitioner via `subject`.
 *
 * Staff AccessPolicies must grant CRUD on this code — portal policies grant no `Basic`.
 */
export const USER_SIGNATURE_CONFIG_SYSTEM = 'https://unify.health/fhir/user-config';
export const USER_SIGNATURE_CONFIG_CODE = 'user-signature-config';
export const USER_SIGNATURE_CONFIG_DATA_URL = 'https://unify.health/fhir/user-signature-data';

// ═══════════════════════════════════════════════════════════════════════
//  Practitioner directory extensions
// ═══════════════════════════════════════════════════════════════════════

export const BIO_EXT = 'https://unify.health/fhir/bio';
export const ALIASES_EXT = 'https://unify.health/fhir/aliases';
export const CLINICAL_EXT = 'https://unify.health/fhir/clinical';
export const CASELOAD_PREFERENCE_EXT = 'https://unify.health/fhir/caseload-preference';
/** Signed-in Practitioner UI display timezone (IANA). Missing = browser Intl. */
export const PRACTITIONER_DISPLAY_TIMEZONE_EXT =
  'https://unify.health/fhir/StructureDefinition/practitioner-display-timezone';

export const QUALIFICATION_EXT_URLS = {
  licenseStatus: 'https://unify.health/fhir/license-status',
  licenseType: 'https://unify.health/fhir/license-type',
  isPrimary: 'https://unify.health/fhir/is-primary',
  superseded: 'https://unify.health/fhir/superseded',
  jurisdictionState: 'https://unify.health/fhir/jurisdiction-state',
  trainingType: 'https://unify.health/fhir/training-type',
  boardCertificationType: 'https://unify.health/fhir/board-certification-type',
  drugSchedules: 'https://unify.health/fhir/drug-schedules',
  businessActivity: 'https://unify.health/fhir/business-activity',
} as const;

// ═══════════════════════════════════════════════════════════════════════
//  Organization practice-bio extension
// ═══════════════════════════════════════════════════════════════════════

export const PRACTICE_BIO_EXT = 'https://unify.health/fhir/ext/practice-bio';

// ═══════════════════════════════════════════════════════════════════════
//  Organization portal extensions
// ═══════════════════════════════════════════════════════════════════════

export const PRACTICE_PORTAL_TILES_EXT = 'https://unify.health/fhir/ext/practice-portal-tiles';

// ═══════════════════════════════════════════════════════════════════════
//  Practice legal document CodeSystem
// ═══════════════════════════════════════════════════════════════════════

export const PRACTICE_LEGAL_DOC_TYPE_SYSTEM = 'https://unify.health/fhir/cs/practice-legal-doc-type';

// ═══════════════════════════════════════════════════════════════════════
//  Patient demographics extensions
// ═══════════════════════════════════════════════════════════════════════

export const GENDER_IDENTITY_EXT = 'https://unify.health/fhir/gender-identity';
export const LGBTQIA_IDENTITIES_EXT = 'https://unify.health/fhir/lgbtqia-identities';
export const RELIGION_SPIRITUALITY_EXT = 'https://unify.health/fhir/religion-spirituality';
export const RACE_ETHNICITY_EXT = 'https://unify.health/fhir/race-ethnicity';
/** Patient extension (valueBoolean) — client flagged high-risk from intake screening. */
export const PATIENT_IS_HIGH_RISK_EXT = 'https://unify.health/fhir/StructureDefinition/is-high-risk';

// ═══════════════════════════════════════════════════════════════════════
//  Bot/automation extensions
// ═══════════════════════════════════════════════════════════════════════

export const AUTOMATED_BY_BOT_EXT = 'https://unify.health/fhir/automated-by-bot';
export const EMAIL_SUBJECT_EXT = 'https://unify.health/fhir/email-subject';

/** Rules Engine CodeSystem (Task codes, CommunicationRequest category). */
export const RULES_ENGINE_CODE_SYSTEM = 'https://unify.health/fhir/CodeSystem/rules-engine';
/** Deterministic identifier system for deferred scheduled emails / actions. */
export const SCHEDULE_KEY_IDENTIFIER_SYSTEM = `${RULES_ENGINE_CODE_SYSTEM}/schedule-key`;
/** CommunicationRequest.category code for deferred Rules Engine emails. */
export const SCHEDULED_EMAIL_CATEGORY_CODE = 'scheduled-email';
/** CommunicationRequest.category code for deferred Rules Engine in-app notifications (ENG-3764 D29). */
export const SCHEDULED_NOTIFICATION_CATEGORY_CODE = 'scheduled-notification';
/**
 * JSON payload (intent + runtime input) on a scheduled-email or scheduled-notification CommunicationRequest.
 * Do not store a rendered HTML body here — re-render at fire time.
 */
export const SCHEDULED_EMAIL_PAYLOAD_EXT =
  'https://unify.health/fhir/StructureDefinition/rules-engine-scheduled-email-payload';

/** AI clinical note generation provenance on QuestionnaireResponse. */
export const AI_GENERATED_PROVENANCE_EXT = 'https://unify.health/fhir/ai-generated-provenance';
/** AI transfer history summary provenance on transfer QuestionnaireResponse. */
export const AI_TRANSFER_SUMMARY_EXT = 'https://unify.health/fhir/ai-transfer-summary';
/** Revision history snapshots on AI-generated QuestionnaireResponse. */
export const AI_NOTE_REVISION_HISTORY_EXT = 'https://unify.health/fhir/ai-note-revision-history';
/**
 * Legacy valueString prompt extensions — used only by the one-shot migration script.
 * Runtime storage is tagged DocumentReference with attachment.data (see AI_PROMPT_* constants below).
 */
export const LEGACY_AI_NOTE_GENERATION_PROMPT_EXT =
  'https://unify.health/fhir/StructureDefinition/ai-note-generation-prompt';
export const LEGACY_AI_TRANSFER_SUMMARY_PROMPT_EXT =
  'https://unify.health/fhir/StructureDefinition/ai-transfer-summary-prompt';
export const LEGACY_AI_TREATMENT_PLAN_GUIDED_PROMPT_EXT =
  'https://unify.health/fhir/StructureDefinition/ai-treatment-plan-guided-prompt';
export const LEGACY_AI_TREATMENT_PLAN_GENERATION_PROMPT_EXT =
  'https://unify.health/fhir/StructureDefinition/ai-treatment-plan-generation-prompt';
/** Selected exemplar QuestionnaireResponse references on HealthcareService (repeated, valueReference). */
export const EXEMPLAR_NOTE_QR_SELECTION_EXT =
  'https://unify.health/fhir/StructureDefinition/exemplar-note-qr';
/** Selected exemplar QuestionnaireResponse references on Questionnaire (repeated, valueReference). */
export const EXEMPLAR_TREATMENT_PLAN_QR_SELECTION_EXT =
  'https://unify.health/fhir/StructureDefinition/exemplar-treatment-plan-selection';
/** Human-readable title for an exemplar treatment plan QuestionnaireResponse (valueString). */
export const EXEMPLAR_TREATMENT_PLAN_TITLE_EXT =
  'https://unify.health/fhir/StructureDefinition/exemplar-treatment-plan-title';
/** AI note quality check results JSON on QuestionnaireResponse. */
export const AI_NOTE_CHECK_RESULTS_EXT = 'https://unify.health/fhir/ai-note-check-results';
/** AI note quality check provenance metadata on QuestionnaireResponse. */
export const AI_NOTE_CHECK_PROVENANCE_EXT = 'https://unify.health/fhir/ai-note-check-provenance';
/** Signed note metadata snapshot JSON on QuestionnaireResponse (date, duration, billing, etc.). */
export const NOTE_METADATA_SNAPSHOT_EXT = 'https://unify.health/fhir/note-metadata-snapshot';
/** Four duration values + mismatch flags for session timing review (JSON on Encounter). */
export const ENCOUNTER_SESSION_TIMING_EXT = 'https://unify.health/fhir/encounter-session-timing';
/**
 * Opt-out from Gemini/LLM-backed AI documentation flows.
 * Applies to both Practitioner and Patient resources.
 * valueBoolean: true = opted out. Missing or false = not opted out (AI enabled by default).
 * Scope: AI note generation, AI note revision, AI treatment plan guided generation, and AI treatment plan revision.
 * Does NOT affect deterministic checks, manual editing, or session transcription.
 */
export const GEMINI_AI_DOCUMENTATION_OPT_OUT_EXT =
  'https://unify.health/fhir/StructureDefinition/gemini-ai-documentation-opt-out';

/**
 * Searchable mirror of the treatment plan's visible expiration date/time answer
 * (QuestionnaireResponse extension, valueDateTime). The visible QuestionnaireResponse
 * item remains the provider-facing source of truth; this extension is synchronized
 * before every treatment-plan save.
 */
export const TREATMENT_PLAN_EXPIRES_AT_EXT =
  'https://unify.health/fhir/StructureDefinition/treatment-plan-expires-at';
/** Idempotent CarePlan key = QuestionnaireResponse id. */
export const TREATMENT_PLAN_CAREPLAN_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/treatment-plan-careplan';
/** Goal key `{qrId}:d{i}:g{j}` or `{qrId}:d{i}:g{j}:o{k}` for objectives. */
export const TREATMENT_PLAN_GOAL_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/treatment-plan-goal';
/** Distinguishes treatment-plan Goal vs nested objective Goal. */
export const TREATMENT_PLAN_GOAL_KIND_SYSTEM =
  'https://unify.health/fhir/CodeSystem/treatment-plan-goal-kind';
export const TREATMENT_PLAN_GOAL_KIND_GOAL = 'treatment-goal';
export const TREATMENT_PLAN_GOAL_KIND_OBJECTIVE = 'objective';
/** CarePlan.activity.detail codes: frequency, symptom, intervention. */
export const TREATMENT_PLAN_ACTIVITY_CODE_SYSTEM =
  'https://unify.health/fhir/CodeSystem/treatment-plan-activity';
export const TREATMENT_PLAN_ACTIVITY_FREQUENCY = 'frequency';
export const TREATMENT_PLAN_ACTIVITY_SYMPTOM = 'symptom';
export const TREATMENT_PLAN_ACTIVITY_INTERVENTION = 'intervention';
/**
 * @deprecated Removed from new CarePlan writes. Multi-diagnosis grouping uses
 * CarePlan.addresses order, Goal identifiers (`:dN:`), and activity.detail.reasonReference.
 * Still recognized when reading legacy resources.
 */
export const TREATMENT_PLAN_DIAGNOSIS_INDEX_EXT =
  'https://unify.health/fhir/StructureDefinition/treatment-plan-diagnosis-index';

/** Annotation.authorString used to distinguish treatment-plan symptom snapshots from other notes. */
export const TREATMENT_PLAN_SYMPTOM_NOTE_AUTHOR = 'Unify treatment plan symptom';
/** Standard R4 parent-goal link for nested objectives. */
export const GOAL_PERTAINS_TO_GOAL_EXT =
  'http://hl7.org/fhir/StructureDefinition/goal-pertainsToGoal';
/** US Core CarePlan category used on Unify treatment-plan CarePlans. */
export const US_CORE_CAREPLAN_CATEGORY_SYSTEM =
  'http://hl7.org/fhir/us/core/CodeSystem/careplan-category';
export const US_CORE_CAREPLAN_CATEGORY_ASSESS_PLAN = 'assess-plan';
/**
 * Minimal AI generation provenance for guided-AI treatment plans
 * (QuestionnaireResponse extension, valueString JSON). Never contains chat
 * transcript content, patient narrative, or signature claims.
 */
export const AI_TREATMENT_PLAN_GENERATION_PROVENANCE_EXT =
  'https://unify.health/fhir/StructureDefinition/ai-treatment-plan-generation-provenance';

// ═══════════════════════════════════════════════════════════════════════
//  Tag / coding systems
// ═══════════════════════════════════════════════════════════════════════

/** Tag system for Unify-specific meta.tag codes. */
export const UNIFY_TAG_SYSTEM = 'https://unify.health/fhir/tags';
/** Tag code identifying a QuestionnaireResponse as an exemplar note for AI generation. */
export const EXEMPLAR_NOTE_TAG_CODE = 'exemplar-note';
/** Full tag object for exemplar note QuestionnaireResponse resources. */
export const EXEMPLAR_NOTE_TAG = {
  system: 'https://unify.health/fhir/tags',
  code: 'exemplar-note',
  display: 'Exemplar Note',
} as const;
/** Tag code identifying a QuestionnaireResponse as a de-identified exemplar treatment plan. */
export const EXEMPLAR_TREATMENT_PLAN_TAG_CODE = 'exemplar-treatment-plan';
/** Full tag object for exemplar treatment plan QuestionnaireResponse resources. */
export const EXEMPLAR_TREATMENT_PLAN_TAG = {
  system: UNIFY_TAG_SYSTEM,
  code: 'exemplar-treatment-plan',
  display: 'Exemplar Treatment Plan',
} as const;

/** Tag code identifying an admin-editable AI system prompt DocumentReference. */
export const AI_PROMPT_TAG_CODE = 'ai-prompt';
/** Full tag object for AI prompt DocumentReferences. */
export const AI_PROMPT_TAG = {
  system: UNIFY_TAG_SYSTEM,
  code: AI_PROMPT_TAG_CODE,
  display: 'AI Prompt',
} as const;
/** Coding system for AI prompt kind tags (searchable on DocumentReference). */
export const AI_PROMPT_KIND_SYSTEM = 'https://unify.health/fhir/ai-prompt-kind';
/** Coding system for AI prompt subject tags (`ResourceType/{id}`) on DocumentReference. */
export const AI_PROMPT_SUBJECT_SYSTEM = 'https://unify.health/fhir/ai-prompt-subject';

export const AI_PROMPT_KIND_NOTE_GENERATION = 'note-generation';
export const AI_PROMPT_KIND_TRANSFER_SUMMARY = 'transfer-summary';
export const AI_PROMPT_KIND_TREATMENT_PLAN_GUIDED = 'treatment-plan-guided';
export const AI_PROMPT_KIND_TREATMENT_PLAN_GENERATION = 'treatment-plan-generation';

export const AI_PROMPT_KINDS = [
  AI_PROMPT_KIND_NOTE_GENERATION,
  AI_PROMPT_KIND_TRANSFER_SUMMARY,
  AI_PROMPT_KIND_TREATMENT_PLAN_GUIDED,
  AI_PROMPT_KIND_TREATMENT_PLAN_GENERATION,
] as const;
export type AiPromptKind = (typeof AI_PROMPT_KINDS)[number];

/** contentType for AI prompt Attachment.data payloads. */
export const AI_PROMPT_BINARY_CONTENT_TYPE = 'text/plain; charset=utf-8';

export const SEED_TAG_SYSTEM = 'https://unify.health/fhir/seed';
export const ACCOUNT_STATUS_TAG_SYSTEM = 'https://unify.health/fhir/account-status';
export const PENDING_ACTIVATION_TAG_CODE = 'pending-activation';

// Template and appointment tag systems (migrated to the canonical unify.health/fhir namespace).
// PRD target for TEMPLATE_CATEGORY_SYSTEM is https://unify.health/fhir/template-category — migrate when data is backfilled.
export const TEMPLATE_CATEGORY_SYSTEM = 'https://unify.health/fhir/CodeSystem/template-category';
export const APPOINTMENT_TAG_SYSTEM = 'https://unify.health/fhir/appointment-id';

/**
 * `meta.tag` system marking a QuestionnaireResponse composed by the Medical Record tab's
 * standalone note composer. These notes are not template-driven, so the tag is the only
 * durable record of which kind of note the author wrote.
 */
export const CHART_NOTE_KIND_TAG_SYSTEM = 'https://unify.health/fhir/CodeSystem/chart-note-kind';
export const CHART_NOTE_KIND_CHART = 'chart-note';
export const CHART_NOTE_KIND_ADMIN = 'admin-note';

/**
 * `meta.tag` system for system-generated chart notes (not AI, not provider-authored).
 * Pair with {@link SYSTEM_GENERATED_ASSESSMENT_COMPLETION_NOTE_CODE} for assessment completion notes.
 */
export const SYSTEM_GENERATED_NOTE_TAG_SYSTEM =
  'https://unify.health/fhir/CodeSystem/system-generated-note';
export const SYSTEM_GENERATED_ASSESSMENT_COMPLETION_NOTE_CODE = 'assessment-completion';

/** Idempotency key for assessment Encounters — value is `Task/{taskId}`. */
export const ASSESSMENT_ENCOUNTER_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/assessment-encounter';
/** Idempotency key for assessment ChargeItems — value is `Task/{taskId}`. */
export const ASSESSMENT_CHARGE_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/assessment-charge';
/** Idempotency key for assessment completion notes — value is `QuestionnaireResponse/{sourceQrId}`. */
export const ASSESSMENT_COMPLETION_NOTE_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/assessment-completion-note';

/** Optional free-text cancellation reason on assessment Tasks. */
export const ASSESSMENT_CANCELLATION_REASON_EXT =
  'https://unify.health/fhir/StructureDefinition/assessment-cancellation-reason';

/** Questionnaire billing CPT extension. */
export const BILLING_CODE_EXTENSION = 'https://unify.health/fhir/StructureDefinition/billing-code';

/** Client-facing assessment Task codes (`TASK_TYPE_SYSTEM`). */
export const CLIENT_ASSESSMENT_ASSIGNMENT_CODE = 'client-assessment-assignment';
export const CLIENT_TAKE_NOW_ASSESSMENT_CODE = 'client-take-now-assessment';

export const TASK_TYPE_SYSTEM = 'https://unify.health/fhir/task-type';

/** Critical cleanup retry claim identifier. Value is the original AuditEvent id. */
export const CRITICAL_BOT_CLEANUP_RETRY_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/critical-bot-cleanup-retry';
export const TASK_INPUT_TARGET_BOT_CODE = 'target-bot';
export const TASK_OUTPUT_RETRY_ATTEMPT_CODE = 'retry-attempt';
/** Read-only compatibility URLs for pre-native cleanup claim Tasks. */
export const LEGACY_CLEANUP_ATTEMPT_EXT =
  'https://unify.health/fhir/StructureDefinition/critical-bot-cleanup-attempt';
export const LEGACY_CLEANUP_LAST_ATTEMPT_EXT =
  'https://unify.health/fhir/StructureDefinition/critical-bot-cleanup-last-attempt';
export const LEGACY_CLEANUP_BOT_KEY_EXT =
  'https://unify.health/fhir/StructureDefinition/critical-bot-cleanup-bot-key';

/** Dual coding: purpose category on `Task.code.coding[]` (locate by system, never index). */
export const TASK_CATEGORY_SYSTEM = 'https://unify.health/fhir/task-category';
export const TASK_CATEGORY_NOTE = 'note';
export const TASK_CATEGORY_DOCUMENT = 'document';
export const TASK_CATEGORY_CLIENT = 'client';
export const TASK_CATEGORY_ADMIN = 'admin';

/** Lifecycle tag: system-managed Tasks are created/completed by bots only. */
export const TASK_LIFECYCLE_SYSTEM = 'https://unify.health/fhir/task-lifecycle';
export const TASK_LIFECYCLE_SYSTEM_MANAGED_CODE = 'system-managed';
/** `task-timer` releases an `on-hold` Task carrying this tag once `restriction.period.start` has passed. */
export const TASK_LIFECYCLE_TIMED_RELEASE_CODE = 'timed-release';

/** Notification purpose category tag (mirrors TASK_CATEGORY_* codes). */
export const NOTIFICATION_CATEGORY_SYSTEM = 'https://unify.health/fhir/notification-category';

/** QR → Task correlation for portal completion (`valueReference` → Task/{id}). */
export const PORTAL_TASK_REFERENCE_EXT = 'https://unify.health/fhir/portal-task-reference';

/**
 * Client may read this in-progress QuestionnaireResponse.
 * AccessPolicy criteria cannot filter extensions, so this tag is the assigned-work mark.
 */
export const CLIENT_ASSIGNED_QR_TAG_SYSTEM = 'https://unify.health/fhir/tags';
export const CLIENT_ASSIGNED_QR_TAG_CODE = 'client-assigned';

/** Searchable key from an assigned QuestionnaireResponse to its portal Task. Value is the Task id. */
export const PORTAL_TASK_QR_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/portal-task-questionnaire-response';

/** A provider shared this DocumentReference with the client. The patient policy grants document read only on this tag. */
export const CLIENT_VISIBLE_TAG_SYSTEM = 'https://unify.health/fhir/document-visibility';
export const CLIENT_VISIBLE_TAG_CODE = 'client-visible';

/**
 * Codes that mark a document as clinical. Never shared with the client, wherever the code sits
 * (type, category, or tag). Bare codes: they match in any system.
 */
export const CLINICAL_DOCUMENT_CODES = [
  'assessment',
  'screening',
  'risk-assessment',
  'clinical',
  'measure',
  'measures',
  'scored-measures',
  'diagnosis',
  'treatment-plan',
  'provider-note',
  'outcome-measure',
] as const;

/** Chart document workflow status on QuestionnaireResponse. */
export const CHART_DOCUMENT_STATUS_EXT =
  'https://unify.health/fhir/StructureDefinition/chart-document-status';
export const CHART_DOCUMENT_STATUS_COMPLETED = 'completed';
/** Chart status of a QR saved for the client to fill (the assignment event, ENG-3764 D1). */
export const CHART_DOCUMENT_STATUS_PENDING_CLIENT = 'pending-client';
/** Chart status the pending-client QR listener sets on a QR it rejects (no type, client-share restricted). */
export const CHART_DOCUMENT_STATUS_ERROR = 'error';
/** Chart status of a chart document whose client Task was cancelled. */
export const CHART_DOCUMENT_STATUS_CANCELLED = 'cancelled';
/**
 * `meta.tag` system for chart document lifecycle marks the server and the chart-document workflow own (ENG-3764 D3).
 * Code `superseded`: on a chart document retired by a re-send (chart status `cancelled`, QR `stopped`). Tells a
 * superseded original (hidden from the chart) from a cancelled send (still listed). A re-send copy never carries it,
 * and a client submission cannot add it.
 */
export const CHART_DOCUMENT_LIFECYCLE_TAG_SYSTEM = 'https://unify.health/fhir/CodeSystem/chart-document-lifecycle';
export const CHART_DOCUMENT_SUPERSEDED_TAG_CODE = 'superseded';
/** Chart document sign settings (written only by the chart-document workflow; they mark a QR as a chart document). */
export const CHART_DOCUMENT_CLIENT_SIGN_REQUIRED_EXT =
  'https://unify.health/fhir/StructureDefinition/chart-document-client-sign-required';
export const CHART_DOCUMENT_PROVIDER_SIGN_REQUIRED_EXT =
  'https://unify.health/fhir/StructureDefinition/chart-document-provider-sign-required';

/**
 * Complex extension on a client assignment QR (ENG-3764 D1, D2, D4): what the assigner chose, which the
 * pending-client QR listener copies onto the client Task. Sub-extensions: `due` (valueDateTime →
 * `restriction.period.end`), `start` (valueDateTime; a future start makes the Task `on-hold` + `timed-release`),
 * `message` (valueString → `Task.note`).
 */
export const CLIENT_ASSIGNMENT_REQUEST_EXT =
  'https://unify.health/fhir/StructureDefinition/client-assignment-request';
/**
 * The staff cancel of a client assignment (ENG-3764 D27) is recorded as a Provenance written in the same transaction
 * as the QR stop: `target` the QR, `activity` HL7 v3 DataOperation `CANCEL`, `reason` the staff reason (a
 * `CANCELLATION_REASON_SYSTEM` coding and the free text), `agent.who` the staff Practitioner. The `meta.tag` below
 * marks it, so the staff create entry that admits it (criteria on the tag) matches no other Provenance. The
 * `portal-task-lifecycle` listener copies its reason to the cancelled Task's `statusReason`.
 */
export const PROVENANCE_ACTIVITY_DATA_OPERATION_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-DataOperation';
export const PROVENANCE_ACTIVITY_CANCEL_CODE = 'CANCEL';
export const CLIENT_ASSIGNMENT_PROVENANCE_TAG_SYSTEM = 'https://unify.health/fhir/CodeSystem/client-assignment-provenance';
export const CLIENT_ASSIGNMENT_CANCEL_PROVENANCE_TAG_CODE = 'staff-cancel';
/**
 * Role-transition Task handoff record (ENG-3764 D29): the Provenance a role transition writes in its transaction,
 * `target` the departing Practitioner, `activity` v3 DataOperation `UPDATE`, `agent` the staff requester (v3
 * ParticipationType `AUT`) and the handoff target (`RESP`, responsible for the departing practitioner's open work).
 * The tag marks it so the `task-events` Subscription matches no other Provenance.
 */
export const PROVENANCE_ACTIVITY_UPDATE_CODE = 'UPDATE';
export const ROLE_TRANSITION_PROVENANCE_TAG_SYSTEM = 'https://unify.health/fhir/CodeSystem/role-transition-provenance';
export const ROLE_TRANSITION_TASK_HANDOFF_TAG_CODE = 'task-handoff';

/** Rules-engine dedupe tag system (code = evaluation key) on the artifacts a rule writes. */
export const RULES_ENGINE_EVALUATION_KEY_TAG_SYSTEM =
  'https://unify.health/fhir/StructureDefinition/rules-engine-evaluation-key';
/** Rules-engine form assignment coding / tag code (`RULES_ENGINE_CODE_SYSTEM`). */
export const RULES_ENGINE_FORM_ASSIGNMENT_CODE = 'rules-engine-form-assignment';
/** Template-library category tag a rules-engine form assignment carries. */
export const FORM_CATEGORY_TAG_SYSTEM = 'https://unify.health/fhir/tag/form-category';

/** Claim.related relationship for a corrected claim pointing at the original. */
export const CLAIM_REPLACED_BY_RELATIONSHIP_SYSTEM =
  'https://unify.health/fhir/CodeSystem/claim-relationship';
export const CLAIM_REPLACED_BY_CODE = 'replaced-by';

/** Portal audience tag. Staff subtypes omit this. */
export const CLIENT_FACING_TASK_TAG_SYSTEM = 'https://unify.health/fhir/tag/client-facing';
export const CLIENT_FACING_TASK_TAG_CODE = 'client-task';

/** Provider task: complete progress note for a past session (`TASK_TYPE_SYSTEM`). */
export const MISSING_NOTE_TASK_CODE = 'missing-note';

/** Idempotent key for missing-note tasks — value is the FHIR Appointment id. */
export const MISSING_NOTE_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/missing-note-task';

/** Provider task: complete treatment plan for an active episode (`TASK_TYPE_SYSTEM`). */
export const TREATMENT_PLAN_DUE_TASK_CODE = 'treatment-plan-due';

/** Idempotent key for treatment-plan-due tasks — value is the EpisodeOfCare id. */
export const TREATMENT_PLAN_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/treatment-plan-task';

/** Provider task: renew treatment plan before expiration (`TASK_TYPE_SYSTEM`). Lead ≈14 days; cycle length is on the plan (180d default / 90d adjustment disorder). */
export const TREATMENT_PLAN_RENEWAL_TASK_CODE = 'treatment-plan-renewal';

/** Idempotent key for treatment-plan-renewal tasks — value is the EpisodeOfCare id. */
export const TREATMENT_PLAN_RENEWAL_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/treatment-plan-renewal-task';

/** Lifecycle notification CodeSystem: the `category/<preference category>` tag and the task-email category. */
export const NOTIFICATION_LIFECYCLE_SYSTEM = 'https://unify.health/fhir/CodeSystem/notification-lifecycle';
/**
 * CommunicationRequest.category code (`NOTIFICATION_LIFECYCLE_SYSTEM`) of a Task email ("Task assigned" / overdue,
 * ENG-3764 D29): created `active` by `task-events` / `task-timer`, sent and closed by `email-sender`.
 */
export const TASK_EMAIL_CATEGORY_CODE = 'task-email';

/** Idempotent key for overdue-task emails (task-email CommunicationRequests) — value is `<overdue Task id>:<UTC YYYY-MM-DD>` (`:`, not the FHIR token delimiter `|`). */
export const OVERDUE_TASK_EMAIL_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/overdue-task-email';

/**
 * Idempotent key for the "Task created" notifications (in-app `Communication` and the asap/stat
 * task-email CommunicationRequest) — value is `<created Task id>:created` (`:released:<versionId>` on release).
 */
export const TASK_CREATED_NOTIFICATION_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/task-created-notification';

/** Supervisee note awaiting supervisor cosignature (`TASK_TYPE_SYSTEM`). */
export const COSIGN_REQUIRED_TASK_CODE = 'cosign-required';
export const COSIGN_TASK_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/cosign-task';

/** Supervisee must revise a note (`TASK_TYPE_SYSTEM`). */
export const SUPERVISOR_EDIT_REQUESTED_TASK_CODE = 'supervisor-edit-requested';
export const SUPERVISOR_EDIT_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/supervisor-edit-task';

/** Staff-facing appointment request (`TASK_TYPE_SYSTEM`). */
export const APPOINTMENT_REQUEST_TASK_CODE = 'appointment-request';
export const APPOINTMENT_REQUEST_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/appointment-request-task';

/** Portal form assignment (`TASK_TYPE_SYSTEM`). */
export const CLIENT_FORM_ASSIGNMENT_CODE = 'client-form-assignment';
export const CLIENT_FORM_ASSIGNMENT_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/client-form-assignment-task';

/**
 * Client assessment assignment Task identifier (keyed on its assignment QR id). Every assessment is one-time since
 * ENG-3764 D30; "occurrence" is from the dropped recurring design, kept because the URI is stored on existing Tasks.
 */
export const CLIENT_ASSESSMENT_OCCURRENCE_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/client-assessment-occurrence-task';

/** Portal signature request (`TASK_TYPE_SYSTEM`). */
export const CLIENT_SIGNATURE_REQUEST_CODE = 'client-signature-request';
export const CLIENT_SIGNATURE_REQUEST_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/client-signature-request-task';

/** Portal chart-document completion (`TASK_TYPE_SYSTEM`). */
export const CLIENT_DOCUMENT_COMPLETION_CODE = 'client-document-completion';
export const CLIENT_DOCUMENT_COMPLETION_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/client-document-completion-task';

/** Unify-staff claim correction (`TASK_TYPE_SYSTEM`). */
export const CLAIM_ERROR_TASK_CODE = 'claim-error';
export const CLAIM_ERROR_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/claim-error-task';

/** Admin orchestration Task for practitioner role transitions (`TASK_TYPE_SYSTEM`). */
export const ROLE_TRANSITION_TASK_CODE = 'role-transition';

/** Idempotent key for role-transition tasks — value is the operation id. */
export const ROLE_TRANSITION_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/role-transition';

/** Parent `Task.code` — overall provider onboarding workflow (`TASK_TYPE_SYSTEM`). */
export const PROVIDER_ONBOARDING_PARENT_TASK_CODE = 'provider-onboarding';

/** Section subtask `Task.code` values for provider onboarding (`TASK_TYPE_SYSTEM`). */
export const PROVIDER_ONBOARDING_SECTION_TASK_CODES = {
  'personal-info': 'provider-onboarding-personal-info',
  availability: 'provider-onboarding-availability',
  profile: 'provider-onboarding-profile',
  'licenses-education': 'provider-onboarding-licenses-education',
  attestation: 'provider-onboarding-attestation',
} as const;

export const DOCUMENT_TYPE_SYSTEM = 'https://unify.health/fhir/document-type';
export const DOCUMENT_CATEGORY_SYSTEM = 'https://unify.health/fhir/document-category';
export const TRACKER_STAGE_TAG_SYSTEM = 'https://unify.health/fhir/tracker-stage';
export const CREDENTIAL_TYPE_SYSTEM = 'https://unify.health/fhir/credential-type';
export const PROVIDER_DOCUMENT_TYPE_SYSTEM = 'https://unify.health/fhir/provider-document-type';

// ═══════════════════════════════════════════════════════════════════════
//  ServiceRequest systems
// ═══════════════════════════════════════════════════════════════════════

export const SERVICE_REQUEST_CATEGORY_SYSTEM = 'https://unify.health/fhir/CodeSystem/service-request-category';
export const THERAPY_SERVICE_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/therapy-service-type';
// Used on ServiceRequest to link to an EpisodeOfCare — distinct from EPISODE_OF_CARE_CONTEXT_EXT above.
export const SERVICE_REQUEST_EOC_EXT = 'https://unify.health/fhir/StructureDefinition/episode-of-care';
/** Idempotency key for portal/chart service-request-originator submissions. */
export const SERVICE_REQUEST_SUBMISSION_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/service-request-submission';
/** Idempotency key shared by the staff client + fulfilled episode creation workflow. */
export const CLIENT_EPISODE_CREATION_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/create-client-and-episode';
export const CANCELLATION_REASON_SYSTEM = 'https://unify.health/fhir/CodeSystem/cancellation-reason';
export const PAYMENT_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/payment-type';
export const TRANSFER_REASON_SYSTEM = 'https://unify.health/fhir/CodeSystem/transfer-reason';

// ═══════════════════════════════════════════════════════════════════════
//  Group therapy (FHIR Group cohort / roster)
// ═══════════════════════════════════════════════════════════════════════

/** Distinguishes therapy cohort Groups from messaging conversation Groups. */
export const GROUP_THERAPY_TAG_SYSTEM = 'https://unify.health/fhir/group-therapy-tag';
export const GROUP_THERAPY_TAG_CODE = 'therapy-cohort';

/**
 * Lifecycle beyond `Group.active`:
 * - active=true → operating
 * - active=false without inactive tag → draft (setup / not yet activated)
 * - active=false with inactive tag → inactive (archived / ended)
 */
export const GROUP_LIFECYCLE_TAG_SYSTEM = 'https://unify.health/fhir/CodeSystem/group-lifecycle';
export const GROUP_LIFECYCLE_INACTIVE_CODE = 'inactive';

export const GROUP_KIND_EXT = 'https://unify.health/fhir/StructureDefinition/group-kind';
export const GROUP_ENROLLMENT_MODE_EXT = 'https://unify.health/fhir/StructureDefinition/group-enrollment-mode';
/** Repeating valueCode extension on Group.member for Practitioner role designations. */
export const GROUP_MEMBER_ROLE_EXT =
  'https://unify.health/fhir/StructureDefinition/group-member-role';
export const GROUP_MEMBER_ROLE_PRIMARY_FACILITATOR_CODE = 'primary-facilitator';
/** @deprecated Read compatibility only; Group screening uses ServiceRequest performers — not written. */
/** @deprecated Read compatibility only; facilitators are Practitioner members. */
export const GROUP_FACILITATOR_EXT = 'https://unify.health/fhir/StructureDefinition/group-facilitator';
/** @deprecated Read compatibility only; use GROUP_MEMBER_ROLE_EXT on Group.member. */
export const GROUP_PRIMARY_FACILITATOR_EXT =
  'https://unify.health/fhir/StructureDefinition/group-primary-facilitator';
/** @deprecated Prefer GROUP_MAX_CAPACITY_EXT. Kept for read compat. */
export const GROUP_CAPACITY_EXT = 'https://unify.health/fhir/StructureDefinition/group-capacity';
export const GROUP_MAX_CAPACITY_EXT = 'https://unify.health/fhir/StructureDefinition/group-max-capacity';
export const GROUP_SCHEDULE_EXT = 'https://unify.health/fhir/StructureDefinition/group-schedule';
/**
 * Marks a Questionnaire.item group as the expandable per-patient section prototype
 * on Group Therapy shared notes. UI clones it to `patient-section.{patientId}`.
 */
export const GROUP_NOTE_PATIENT_SECTION_EXT =
  'https://unify.health/fhir/StructureDefinition/group-note-patient-section';
/** Stable Questionnaire.item.linkId for the per-patient section prototype. */
export const GROUP_NOTE_PATIENT_SECTION_PROTOTYPE_LINK_ID = 'patient-section-prototype';
/** Prefix for expanded per-patient section linkIds (`patient-section.{patientId}`). */
export const GROUP_NOTE_PATIENT_SECTION_LINK_ID_PREFIX = 'patient-section.';
/**
 * Cohort → Group Template (`HealthcareService` with category group-template).
 * Template owns session HCS (auto-stamped), photo, and schedule defaults.
 */
export const GROUP_TEMPLATE_EXT = 'https://unify.health/fhir/StructureDefinition/group-template';
export const GROUP_ENROLLMENT_LOCK_DATE_EXT = 'https://unify.health/fhir/StructureDefinition/group-enrollment-lock-date';
/** Cohort-specific description override. */
export const GROUP_DESCRIPTION_EXT = 'https://unify.health/fhir/StructureDefinition/group-description';
/** Session modality override for the cohort (`SESSION_MODALITY_SYSTEM` code). */
export const GROUP_MODALITY_EXT = 'https://unify.health/fhir/StructureDefinition/group-modality';
/** In-person / hybrid site Location reference for the cohort. */
export const GROUP_LOCATION_EXT = 'https://unify.health/fhir/StructureDefinition/group-location';
/** Therapy room Location reference for the cohort (calendar ROOM participant). */
export const GROUP_ROOM_EXT = 'https://unify.health/fhir/StructureDefinition/group-room';
/** Portable Device reference for the cohort (calendar DEV participant). Repeatable. */
export const GROUP_DEVICE_EXT = 'https://unify.health/fhir/StructureDefinition/group-device';
export const GROUP_SHARED_NOTE_EXT = 'https://unify.health/fhir/StructureDefinition/group-shared-note';
/** Waitlist / transfer destination Group Template HealthcareService reference. */
export const REQUESTED_GROUP_TEMPLATE_EXT =
  'https://unify.health/fhir/StructureDefinition/requested-group-template';
/** Canonical EpisodeOfCare → Group cohort link. */
export const EOC_GROUP_EXT = 'https://unify.health/fhir/StructureDefinition/episodeofcare-group';
/** @deprecated Read compatibility only; prefer EOC_GROUP_EXT. Writers must not dual-write. */
export const EOC_GROUP_REFERENCE_EXT = 'https://unify.health/fhir/StructureDefinition/group-reference';
export const APPOINTMENT_GROUP_EXT = 'https://unify.health/fhir/StructureDefinition/appointment-group';

// ═══════════════════════════════════════════════════════════════════════
//  Group Template (HealthcareService category = group-template)
// ═══════════════════════════════════════════════════════════════════════

/** Category system for Group Template HealthcareServices (not calendar session types). */
export const GROUP_TEMPLATE_CATEGORY_SYSTEM =
  'https://unify.health/fhir/CodeSystem/group-template-category';
export const GROUP_TEMPLATE_CATEGORY_CODE = 'group-template';

export const GROUP_TEMPLATE_SESSION_HEALTHCARE_SERVICE_EXT =
  'https://unify.health/fhir/StructureDefinition/group-template-session-healthcare-service';
/**
 * CodeSystem for Group / Group Template enrollment mode (`closed` | `open`).
 * Templates carry it on `HealthcareService.characteristic`.
 */
export const GROUP_ENROLLMENT_MODE_SYSTEM =
  'https://unify.health/fhir/CodeSystem/group-enrollment-mode';
export const GROUP_TEMPLATE_CAPACITY_EXT =
  'https://unify.health/fhir/StructureDefinition/group-template-capacity';
export const GROUP_TEMPLATE_TIMEZONE_EXT =
  'https://unify.health/fhir/StructureDefinition/group-template-timezone';
/** Nested defaults for new cohorts: sessionCount only (days live on `availableTime`). */
export const GROUP_TEMPLATE_SCHEDULE_DEFAULTS_EXT =
  'https://unify.health/fhir/StructureDefinition/group-template-schedule-defaults';

// ═══════════════════════════════════════════════════════════════════════
//  VOB / Coverage eligibility extensions & CodeSystems
// ═══════════════════════════════════════════════════════════════════════

export const EXTERNAL_VERIFICATION_ID_EXT = 'https://unify.health/fhir/external-verification-id';
/** Searchable `CoverageEligibilityResponse.identifier` for webhook correlation. */
export const EXTERNAL_VERIFICATION_ID_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/external-verification-id';
export const EXTERNAL_VERIFICATION_TYPE_EXT = 'https://unify.health/fhir/external-verification-type';
export const VERIFICATION_SOURCE_EXT = 'https://unify.health/fhir/verification-source';
export const VERIFIED_BILLING_SERVICE_CODE_EXT = 'https://unify.health/fhir/verified-billing-service-code';
export const BENEFIT_TIER_EXT = 'https://unify.health/fhir/benefit-tier';
export const PLACE_OF_SERVICE_CODE_EXT = 'https://unify.health/fhir/place-of-service-code';
/** DocumentReference.identifier for raw VOB partner payloads, keyed by CER id. */
export const VOB_RAW_PAYLOAD_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/vob-raw-payload';
/** CMS Place of Service Code Set (Claim.item.locationCodeableConcept.coding.system). */
export const CMS_PLACE_OF_SERVICE_CODE_SYSTEM =
  'https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set';

/** Claim.item → ChargeItem join (required, 1..* per item). */
export const SOURCE_CHARGE_ITEM_EXT = 'https://unify.health/fhir/source-charge-item';
/** Write-once ChargeItem grouping key (valueString). */
export const BILLING_GROUPING_KEY_EXT = 'https://unify.health/fhir/billing-grouping-key';
/** Claim.identifier: shared grouping key for a professional/institutional group. */
export const CLAIM_GROUP_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/claim-group';
/** Claim.identifier: unique original lineage `{groupingKey}:{coverageId}`. */
export const CLAIM_LINEAGE_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/claim-lineage';
/** Claim.identifier: Stedi patient control number (≤17 alphanumeric). */
export const PATIENT_CONTROL_NUMBER_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/patient-control-number';
/** DocumentReference.identifier for the attached Stedi 837P JSON payload. */
export const STEDI_837P_PAYLOAD_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/stedi-837p-payload';
/** ClaimResponse.identifier: submit lock `{Claim.id}:{stediCorrelationId}`. */
export const CLAIM_SUBMIT_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/claim-submit';
/** ClaimResponse.identifier: `submit` | `277` | `835`. */
export const STEDI_RESPONSE_TYPE_IDENTIFIER_SYSTEM = 'https://www.stedi.com/response-type';
/** Claim.identifier: Stedi claim correlation id after successful submit. */
export const STEDI_CLAIM_CORRELATION_IDENTIFIER_SYSTEM = 'https://www.stedi.com/claims';
/** Claim.identifier: in-flight submit mutex (value = Claim.id) until billed recon completes or 4xx. */
export const CLAIM_SUBMIT_INFLIGHT_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/claim-submit-inflight';
/** Task.identifier for billing-exception Tasks (search-then-create). */
export const BILLING_EXCEPTION_TASK_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/billing-exception-task';
/** HL7 EIN identifier required on Claim.provider Organizations. */
export const US_EIN_IDENTIFIER_SYSTEM = 'http://hl7.org/fhir/sid/us-ein';
/** Claim.type coding system. */
export const CLAIM_TYPE_CODE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/claim-type';
/** Claim.priority coding system. */
export const PROCESS_PRIORITY_CODE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/processpriority';
/** Claim.careTeam.role coding system. */
export const CLAIM_CARE_TEAM_ROLE_CODE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/claimcareteamrole';
/** ICD-10-CM diagnosis coding system. */
export const ICD10_CM_CODE_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm';
export const EXTERNAL_RAW_RESPONSE_EXT = 'https://unify.health/fhir/external-raw-response';

export const EXTERNAL_VERIFICATION_TYPE_SYSTEM =
  'https://unify.health/fhir/CodeSystem/external-verification-type';
export const VERIFICATION_SOURCE_SYSTEM = 'https://unify.health/fhir/CodeSystem/verification-source';
export const BILLING_SERVICE_CODE_SYSTEM = 'https://unify.health/fhir/CodeSystem/billing-service-code';
export const SOHAR_SPECIALTY_CODE_SYSTEM = 'https://soharhealth.com/specialty-code';
export const SOHAR_PAYER_ID_SYSTEM = 'https://soharhealth.com/payer-id';
/** Verifiable Medicaid payer ID on accepted-payer Organizations. */
export const VERIFIABLE_PAYER_MEDICAID_ID_SYSTEM =
  'https://unify.health/fhir/identifier/verifiable-payer-id-medicaid';
/** Verifiable Medicare payer ID on accepted-payer Organizations. */
export const VERIFIABLE_PAYER_MEDICARE_ID_SYSTEM =
  'https://unify.health/fhir/identifier/verifiable-payer-id-medicare';
/** Verifiable Commercial payer ID on accepted-payer Organizations. */
export const VERIFIABLE_PAYER_COMMERCIAL_ID_SYSTEM =
  'https://unify.health/fhir/identifier/verifiable-payer-id-commercial';

/** Stedi payer network ID — primary routing identifier for `$stedi-submit-claim`. */
export const STEDI_PAYER_NETWORK_ID_SYSTEM = 'https://www.stedi.com/healthcare/network';
/** Stedi payer ID (secondary routing identifier). */
export const STEDI_PAYER_ID_SYSTEM = 'https://stedi.com/payerId';
/** Candid/CHC payer ID (tertiary fallback for Stedi claim routing). */
export const CHC_PAYER_ID_SYSTEM = 'https://www.joincandidhealth.com/chc-payerid';

/** Stedi-resolvable payer ID systems in Medplum bot priority order. */
export const STEDI_PAYER_ID_SYSTEMS = [
  STEDI_PAYER_NETWORK_ID_SYSTEM,
  STEDI_PAYER_ID_SYSTEM,
  CHC_PAYER_ID_SYSTEM,
] as const;

/** Standard HL7 benefit type / network terminology for CoverageEligibilityResponse benefits. */
export const BENEFIT_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/benefit-type';
export const BENEFIT_NETWORK_SYSTEM = 'http://terminology.hl7.org/CodeSystem/benefit-network';
/**
 * Unify VOB benefit-type CodeSystem — only for benefit kinds HL7 `benefit-type` lacks.
 * Named distinctly from the HL7 system to avoid collision. Currently: out-of-pocket maximum,
 * which has no standard `benefit-type` code (`benefit` = insurer max payout, not member liability cap).
 */
export const UNIFY_VOB_BENEFIT_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/vob-benefit-type';
export const OOP_MAX_BENEFIT_TYPE_CODE = 'out-of-pocket-maximum';

/** `Task.code` (TASK_TYPE_SYSTEM) for routine eligibility verification work. */
export const VERIFY_ELIGIBILITY_TASK_CODE = 'verify-eligibility';
/** `Task.code` (TASK_TYPE_SYSTEM) for failed/ineligible eligibility review. */
export const ELIGIBILITY_ERROR_TASK_CODE = 'eligibility-error';
/** `Task.input.type` code for billing service code on eligibility Tasks. */
export const ELIGIBILITY_TASK_BILLING_CODE_INPUT = 'billing-service-code';

export const EOC_DETAILED_STATUS_SYSTEM = 'https://unify.health/fhir/CodeSystem/eoc-detailed-status';
export const EOC_DETAILED_STATUS_NEEDS_VERIFICATION = 'needs-verification';

/** Default reverification window (days) when no project config is present. */
export const DEFAULT_REVERIFICATION_WINDOW_DAYS = 30;
/** Default ops SLA (hours) for open verify-eligibility / eligibility-error Tasks. */
export const DEFAULT_ELIGIBILITY_OPS_SLA_HOURS = 48;
/** CMS place-of-service: telehealth (default when modality unknown). */
export const DEFAULT_PLACE_OF_SERVICE_CODE = '02';

// ═══════════════════════════════════════════════════════════════════════
//  Assessment extensions (QuestionnaireResponse)
// ═══════════════════════════════════════════════════════════════════════

export const QR_TOTAL_SCORE_EXT = 'https://unify.health/fhir/StructureDefinition/questionnaire-response-total-score';
export const QR_INTERPRETATION_EXT = 'https://unify.health/fhir/StructureDefinition/questionnaire-response-interpretation';

// ═══════════════════════════════════════════════════════════════════════
//  Signature / supervision extensions and systems
// ═══════════════════════════════════════════════════════════════════════

// Signature and supervision extensions (migrated to the canonical unify.health/fhir namespace).
// PRD target URLs are under https://unify.health/fhir/StructureDefinition/ — migrate when existing data is backfilled.
export const PROVIDER_SIGNATURE_EXT_URL = 'https://unify.health/fhir/StructureDefinition/provider-signature';
export const SUPERVISOR_SIGNATURE_EXT_URL = 'https://unify.health/fhir/StructureDefinition/supervisor-signature';
export const REQUESTED_EDITS_EXT_URL = 'https://unify.health/fhir/supervision/requested-edits';
export const REQUESTED_EDITS_TAG_SYSTEM = 'https://unify.health/fhir/supervision/requested-edits';
export const REVIEW_COMMENTS_EXT_URL = 'https://unify.health/fhir/supervision/review-comments';

export const SIGNATURE_IP_EXT = 'https://unify.health/fhir/StructureDefinition/signature-ip';
export const SIGNATURE_TYPED_TEXT_EXT = 'https://unify.health/fhir/StructureDefinition/signature-typed-text';

export const SUPERVISION_TASK_SYSTEM = 'https://unify.health/fhir/supervision/task-type';
export const SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM = 'https://unify.health/fhir/supervision/form-subtype';
export const SUPERVISION_TASK_CATEGORY_SYSTEM = 'https://unify.health/fhir/supervision/task-category';
export const COMPLIANCE_OBSERVATION_SYSTEM = 'https://unify.health/fhir/supervision/compliance';
export const NOTE_QUALITY_OBSERVATION_SYSTEM = 'https://unify.health/fhir/supervision/note-quality';
export const SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM = 'https://unify.health/fhir/supervision/provenance-activity';
export const PROVENANCE_DETAIL_EXT_URL = 'https://unify.health/fhir/supervision/provenance-detail';

// ═══════════════════════════════════════════════════════════════════════
//  Template tag systems (unify.health/fhir)
// ═══════════════════════════════════════════════════════════════════════

export const TEMPLATE_DEFAULT_TAG_SYSTEM = 'https://unify.health/fhir/template-default';
export const SCORED_MEASURE_DELIVERY_TAG = 'https://unify.health/fhir/scored-measure-delivery';
export const SCORED_MEASURE_SCORING_TAG = 'https://unify.health/fhir/scored-measure-scoring';

// ═══════════════════════════════════════════════════════════════════════
//  Identifier systems
// ═══════════════════════════════════════════════════════════════════════

export const USER_EMAIL_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/user-email';
export const SSN_IDENTIFIER_SYSTEM = 'http://hl7.org/fhir/sid/us-ssn';

// ═══════════════════════════════════════════════════════════════════════
//  Calendar appointment sub-type systems
// ═══════════════════════════════════════════════════════════════════════

export const APPOINTMENT_CATEGORY_SYSTEM = 'https://unify.health/fhir/appointment-category';
export const MEETING_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/meeting-type';
export const TIME_ENTRY_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/time-entry-type';

// ═══════════════════════════════════════════════════════════════════════
//  Appointment service category / type (PRD_APPOINTMENTS_FHIR_MAPPING)
// ═══════════════════════════════════════════════════════════════════════

/** `Appointment.serviceCategory` coding system (highest-level grouping). */
export const APPOINTMENT_SERVICE_CATEGORY_SYSTEM = 'https://unify.health/fhir/CodeSystem/service-category';
/** `Appointment.serviceType` coding system (mid-level service type). */
export const APPOINTMENT_SERVICE_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/service-type';
/** `serviceCategory` code for client onboarding (screening / consultation) appointments. */
export const APPOINTMENT_SERVICE_CATEGORY_INTAKE = 'intake';
/** `serviceType` code for the high-risk intake screening call. */
export const SCREENING_CALL_SERVICE_TYPE_CODE = 'screening-call';

/** AMA CPT code system (ChargeItemDefinition/ChargeItem and Appointment serviceType snapshots). */
export const CPT_CODE_SYSTEM = 'http://www.ama-assn.org/go/cpt';
/** Standard HCPCS Level II coding system URI. */
export const HCPCS_LEVEL_II_CODE_SYSTEM = 'https://www.cms.gov/Medicare/Coding/HCPCSReleaseCodeSets';
/** CMS Physician Fee Schedule CPT/HCPCS picker used when creating ChargeItemDefinitions. */
export const CMS_PFS_PROCEDURE_CODES_VALUE_SET =
  'https://unify.health/fhir/ValueSet/cms-pfs-procedure-codes';
/** Stable platform catalog key for the Group Therapy session HealthcareService (CPT 90853). */
export const GROUP_THERAPY_SESSION_CATALOG_KEY = 'clinical-90853';
/** Stable platform catalog key for the Group Therapy screening HealthcareService. */
export const GROUP_THERAPY_SCREENING_CATALOG_KEY = 'clinical-high-risk-screening';
/** Canonical Questionnaire URL prefix for Unify-authored templates. */
export const QUESTIONNAIRE_CANONICAL_URL_PREFIX = 'https://unify.health/fhir/Questionnaire/';

/** FHIR Questionnaire.item hidden flag (not rendered; still participates in enableWhen). */
export const QUESTIONNAIRE_HIDDEN_EXT = 'http://hl7.org/fhir/StructureDefinition/questionnaire-hidden';

/** Shared hidden item on appointment notes — prefilled from Appointment modality. */
export const SESSION_MODALITY_LINK_ID = 'session-modality';

/** Progress-note crisis gate; Yes reveals the nested suicide risk assessment group. */
export const IS_CRISIS_SESSION_LINK_ID = 'is-crisis-session';

/** Nested SRA group on the progress note (items namespaced under this prefix). */
export const PROGRESS_NOTE_SRA_GROUP_LINK_ID = 'unify-progress-note-sra';

/** Runtime-expanded prototype on the late-cancel / no-show and cancelled session notes. */
export const ABSENCE_REASON_PROTOTYPE_LINK_ID = 'absence-reason-prototype';

export const UNIFY_NOTE_TEMPLATE_IDS = {
  screening: 'unify-screening-note',
  consultation: 'unify-consultation-note',
  individualIntake: 'unify-individual-therapy-intake-note',
  couplesFamilyIntake: 'unify-couples-family-therapy-intake-note',
  progress: 'unify-progress-note',
  groupProgress: 'unify-group-therapy-progress-note',
  psychiatryIntake: 'unify-psychiatry-intake-note',
  psychiatryProgress: 'unify-psychiatry-progress-note',
  lateCancelOrNoShow: 'unify-late-cancel-or-no-show-note',
  cancelled: 'unify-cancelled-session-note',
} as const;

export type UnifyNoteTemplateId = (typeof UNIFY_NOTE_TEMPLATE_IDS)[keyof typeof UNIFY_NOTE_TEMPLATE_IDS];

/** Replaced appointment-note slugs — retired/deleted on templates:documents:setup. */
export const RETIRED_APPOINTMENT_NOTE_TEMPLATE_IDS = [
  'meridian-counseling-telehealth-progress-note',
  'meridian-counseling-in-person-progress-note',
  'meridian-counseling-telehealth-couples-progress-note',
  'meridian-counseling-in-person-couples-progress-note',
  'meridian-counseling-telehealth-family-progress-note',
  'meridian-counseling-in-person-family-progress-note',
  'meridian-counseling-telehealth-intake-note',
  'meridian-counseling-in-person-intake-note',
  'meridian-counseling-telehealth-couples-intake-note',
  'meridian-counseling-in-person-couples-intake-note',
  'meridian-counseling-telehealth-family-intake-note',
  'meridian-counseling-in-person-family-intake-note',
  'meridian-counseling-telehealth-group-therapy-note',
  'meridian-recovery-iop-telehealth-group-note',
  'group-therapy-screening-note',
  'meridian-counseling-new-client-consultation',
  'meridian-counseling-psychiatry-intake-assessment',
  'meridian-counseling-psychiatry-progress-note',
  'late-cancel-session-note',
  'no-show-session-note',
] as const;

/** Live HealthcareService note-template-id values → surviving unify-* slug. */
export const APPOINTMENT_NOTE_TEMPLATE_ID_REMAP: Record<string, string> = {
  'meridian-counseling-telehealth-progress-note': UNIFY_NOTE_TEMPLATE_IDS.progress,
  'meridian-counseling-in-person-progress-note': UNIFY_NOTE_TEMPLATE_IDS.progress,
  'meridian-counseling-telehealth-couples-progress-note': UNIFY_NOTE_TEMPLATE_IDS.progress,
  'meridian-counseling-in-person-couples-progress-note': UNIFY_NOTE_TEMPLATE_IDS.progress,
  'meridian-counseling-telehealth-family-progress-note': UNIFY_NOTE_TEMPLATE_IDS.progress,
  'meridian-counseling-in-person-family-progress-note': UNIFY_NOTE_TEMPLATE_IDS.progress,
  'meridian-counseling-telehealth-intake-note': UNIFY_NOTE_TEMPLATE_IDS.individualIntake,
  'meridian-counseling-in-person-intake-note': UNIFY_NOTE_TEMPLATE_IDS.individualIntake,
  'meridian-counseling-telehealth-couples-intake-note': UNIFY_NOTE_TEMPLATE_IDS.couplesFamilyIntake,
  'meridian-counseling-in-person-couples-intake-note': UNIFY_NOTE_TEMPLATE_IDS.couplesFamilyIntake,
  'meridian-counseling-telehealth-family-intake-note': UNIFY_NOTE_TEMPLATE_IDS.couplesFamilyIntake,
  'meridian-counseling-in-person-family-intake-note': UNIFY_NOTE_TEMPLATE_IDS.couplesFamilyIntake,
  'meridian-counseling-telehealth-group-therapy-note': UNIFY_NOTE_TEMPLATE_IDS.groupProgress,
  'meridian-recovery-iop-telehealth-group-note': UNIFY_NOTE_TEMPLATE_IDS.groupProgress,
  'group-therapy-screening-note': UNIFY_NOTE_TEMPLATE_IDS.screening,
  'meridian-counseling-new-client-consultation': UNIFY_NOTE_TEMPLATE_IDS.consultation,
  'meridian-counseling-psychiatry-intake-assessment': UNIFY_NOTE_TEMPLATE_IDS.psychiatryIntake,
  'meridian-counseling-psychiatry-progress-note': UNIFY_NOTE_TEMPLATE_IDS.psychiatryProgress,
  'late-cancel-session-note': UNIFY_NOTE_TEMPLATE_IDS.lateCancelOrNoShow,
  'no-show-session-note': UNIFY_NOTE_TEMPLATE_IDS.lateCancelOrNoShow,
};

export function remapAppointmentNoteTemplateId(templateId: string | null | undefined): string | undefined {
  const raw = templateId?.trim();
  if (!raw) return undefined;
  return APPOINTMENT_NOTE_TEMPLATE_ID_REMAP[raw] ?? raw;
}

/** Default clinical-note template id for Group Therapy session HealthcareServices. */
export const GROUP_THERAPY_SESSION_NOTE_TEMPLATE_ID = UNIFY_NOTE_TEMPLATE_IDS.groupProgress;

/** Historical group-session questionnaire slugs (cosign / fan-out still match these). */
export const LEGACY_GROUP_THERAPY_SESSION_NOTE_TEMPLATE_IDS = [
  'meridian-counseling-telehealth-group-therapy-note',
  'meridian-recovery-iop-telehealth-group-note',
] as const;

export function questionnaireRefIsGroupTherapySessionNote(qref: string | undefined): boolean {
  if (!qref) return false;
  const key = qref.toLowerCase();
  if (key.includes(GROUP_THERAPY_SESSION_NOTE_TEMPLATE_ID)) return true;
  return LEGACY_GROUP_THERAPY_SESSION_NOTE_TEMPLATE_IDS.some((id) => key.includes(id));
}

// ═══════════════════════════════════════════════════════════════════════
//  Calendar event type catalog (HealthcareService templates)
// ═══════════════════════════════════════════════════════════════════════

export const CALENDAR_EVENT_CATEGORY_SYSTEM = 'https://unify.health/fhir/CodeSystem/calendar-event-category';
export const SESSION_MODALITY_SYSTEM = 'https://unify.health/fhir/CodeSystem/session-modality';
export const SESSION_TYPE_GROUP_SYSTEM = 'https://unify.health/fhir/CodeSystem/session-type-group';
export const SCHEDULING_PARAMETERS_EXT = 'https://medplum.com/fhir/StructureDefinition/SchedulingParameters';
/** Default ChargeItemDefinition for a billable clinical-session HealthcareService. */
export const CHARGE_ITEM_DEFINITION_REF_EXT =
  'https://unify.health/fhir/StructureDefinition/charge-item-definition-reference';
export const DEFAULT_SESSION_RATE_EXT = 'https://unify.health/fhir/StructureDefinition/default-session-rate';
export const NOTE_TEMPLATE_ID_EXT = 'https://unify.health/fhir/StructureDefinition/note-template-id';
export const ONLINE_BOOKING_EXT = 'https://unify.health/fhir/StructureDefinition/online-booking';
export const INTAKE_ONLY_EXT = 'https://unify.health/fhir/StructureDefinition/intake-only';
export const EOC_TYPE_INTAKE_TYPE_EXT =
  'https://unify.health/fhir/StructureDefinition/eoc-type-intake-type';
export const EOC_TYPE_SESSION_MAP_EXT =
  'https://unify.health/fhir/StructureDefinition/eoc-type-session-map';
export const EOC_TYPE_SOHAR_SPECIALTY_CODE_EXT =
  'https://unify.health/fhir/StructureDefinition/eoc-type-sohar-specialty-code';
/** Repeating extension — additional approved clinical-session HealthcareService refs per EoC type. */
export const EOC_TYPE_APPROVED_SESSIONS_EXT =
  'https://unify.health/fhir/StructureDefinition/eoc-type-approved-sessions';

/** Identifier marking a global platform HealthcareService catalog entry (no per-practice copy). */
export const PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/healthcare-service-scope';
export const PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_VALUE = 'platform';
/** Stable per-row key for cloud-safe HealthcareService catalog upsert (search → PUT / POST). */
export const HEALTHCARE_SERVICE_KEY_SYSTEM = 'https://unify.health/fhir/identifier/healthcare-service-key';

/** Stable per-row key for ChargeItemDefinition upsert (value matches HealthcareService catalog key). */
export const CHARGE_ITEM_DEFINITION_KEY_SYSTEM =
  'https://unify.health/fhir/identifier/charge-item-definition-key';
/** Canonical base URL for ChargeItemDefinition.url and ChargeItem.definitionUri. */
export const CHARGE_ITEM_DEFINITION_CANONICAL_BASE = 'https://unify.health/fhir/ChargeItemDefinition';
/** Stable catalog URI base for ChargeItemDefinition.derivedFromUri (not a Medplum resource id). */
export const HEALTHCARE_SERVICE_CATALOG_URI_BASE = 'https://unify.health/fhir/HealthcareServiceCatalog';

/** Per-practice enabled-services List (`List.code` purpose — PRD_PRACTICES_FHIR_MAPPING). */
export const LIST_TYPE_SYSTEM = 'https://unify.health/fhir/cs/list-type';
/** Per-practice working list of enabled platform HealthcareService references. */
export const ENABLED_HEALTHCARE_SERVICES_LIST_CODE = 'enabled-session-types';
/** Identifier system used to tag each practice's enabled-services List with its practice org ID. */
export const PRACTICE_SERVICES_LIST_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/practice-enabled-services';

// ═══════════════════════════════════════════════════════════════════════
//  Intake service type system
// ═══════════════════════════════════════════════════════════════════════

export const INTAKE_SERVICE_TYPE_SYSTEM = 'https://unify.health/fhir/intake-service-type';

// ═══════════════════════════════════════════════════════════════════════
//  Practice sites, rooms & equipment (Locations/Rooms/Equipment PRD)
// ═══════════════════════════════════════════════════════════════════════

export const ROOM_CAPACITY_EXT = 'https://unify.health/fhir/StructureDefinition/room-capacity';
export const ADA_ACCESSIBLE_EXT = 'https://unify.health/fhir/StructureDefinition/ada-accessible';
export const DEVICE_PORTABILITY_EXT = 'https://unify.health/fhir/StructureDefinition/device-portability';

/** In-person client office instructions (used by Rules Engine in-person appointment emails). */
export const LOCATION_PARKING_INSTRUCTIONS_EXT = 'https://unify.health/fhir/StructureDefinition/location-parking-instructions';
export const LOCATION_ARRIVAL_INSTRUCTIONS_EXT = 'https://unify.health/fhir/StructureDefinition/location-arrival-instructions';
export const LOCATION_WAITING_ROOM_INSTRUCTIONS_EXT = 'https://unify.health/fhir/StructureDefinition/location-waiting-room-instructions';

export const ROOM_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/room-type';
export const LOCATION_IDENTIFIER_SYSTEM = 'https://unify.health/location-id';
export const DEVICE_IDENTIFIER_SYSTEM = 'https://unify.health/device-id';

/** HL7 Location physical type CodeSystem (building, room, …). */
export const LOCATION_PHYSICAL_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/location-physical-type';

// ═══════════════════════════════════════════════════════════════════════
//  Provider profile coded value sets (see @unify/fhir-model/provider-profile-datasets)
// ═══════════════════════════════════════════════════════════════════════

export const PROVIDER_SPECIALTY_SYSTEM = 'https://unify.health/fhir/CodeSystem/provider-specialty';
export const GENDER_IDENTITY_SYSTEM = 'https://unify.health/fhir/CodeSystem/gender-identity';
export const SEXUALITY_SYSTEM = 'https://unify.health/fhir/CodeSystem/sexuality';
export const THERAPY_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/therapy-type';
export const ETHNICITY_SYSTEM = 'https://unify.health/fhir/CodeSystem/ethnicity';
export const FAITH_SYSTEM = 'https://unify.health/fhir/CodeSystem/faith';

// ═══════════════════════════════════════════════════════════════════════
//  FHIR standard extension URLs
// ═══════════════════════════════════════════════════════════════════════

export const ITEM_CONTROL_EXT_URL = 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl';

// ═══════════════════════════════════════════════════════════════════════
//  RBAC CodeSystem URLs
// ═══════════════════════════════════════════════════════════════════════

/** Profile role extension — all user types (Practitioner, Patient, RelatedPerson). */
export const PROFILE_ROLE_SYSTEM = 'https://unify.health/fhir/CodeSystem/profile-role';
/** PractitionerRole.code[] — practitioner-facing org roles only. */
export const PRACTITIONER_ROLE_SYSTEM = 'https://unify.health/fhir/CodeSystem/practitioner-role';

/** Canonical profile-role codes (all user types). */
export const PROFILE_ROLE_CODES = [
  'unify-admin',
  'practice-owner',
  'practice-admin',
  'supervisor',
  'provider',
  'client',
] as const;

/** Practitioner-only role codes for PractitionerRole.code[]. */
export const PRACTITIONER_ROLE_CODES = [
  'provider',
  'supervisor',
  'practice-owner',
  'practice-admin',
  'unify-admin',
] as const;

/** Codes that must NOT appear in PractitionerRole.code[]. */
export const CLIENT_ONLY_ROLE_CODES = ['client'] as const;

export type ProfileRoleCode = (typeof PROFILE_ROLE_CODES)[number];
export type PractitionerRoleCode = (typeof PRACTITIONER_ROLE_CODES)[number];

// ═══════════════════════════════════════════════════════════════════════
//  Organization / Practice constants
// ═══════════════════════════════════════════════════════════════════════

/** Standard HL7 organization type system (http://terminology.hl7.org/CodeSystem/organization-type). */
export const ORG_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/organization-type';
/** Organization type code for healthcare provider organizations (practices). */
export const ORG_TYPE_PROV = 'prov';
/** Organization type code for insurance payer organizations (accepted payer directory; HL7 `ins`). */
export const ORG_TYPE_PAYER = 'ins';
/** HL7 organization-type code for the Unify platform org (not a practice; excluded from `Organization?type=prov`). */
export const ORG_TYPE_OTHER = 'other';
/**
 * Unify organization-kind CodeSystem. Billing entities use this **instead of** HL7
 * `prov` / `ins` / `other` so they never appear in Practices, Payers, or platform lookups.
 */
export const ORG_KIND_SYSTEM = 'https://unify.health/fhir/CodeSystem/organization-kind';
/** Legal/merchant billing entity (NPI, EIN, Stripe connected-account id). */
export const ORG_KIND_BILLING_ENTITY = 'billing-entity';
/** Stripe Connect connected-account id (`acct_…`) on `Organization.identifier[]`. */
export const STRIPE_CONNECTED_ACCOUNT_SYSTEM = 'https://stripe.com/account/id';
/** Identifier system for the singleton Unify platform Organization. */
export const PLATFORM_ORGANIZATION_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/platform-org';
/** Identifier value for the singleton Unify platform Organization. */
export const PLATFORM_ORGANIZATION_IDENTIFIER_VALUE = 'unify';
/** Search token for `Organization?identifier=` of the singleton Unify platform org. */
export const PLATFORM_ORGANIZATION_IDENTIFIER_QUERY = `${PLATFORM_ORGANIZATION_IDENTIFIER_SYSTEM}|${PLATFORM_ORGANIZATION_IDENTIFIER_VALUE}`;

// ═══════════════════════════════════════════════════════════════════════
//  Coverage constants
// ═══════════════════════════════════════════════════════════════════════

/** FHIR R4 coverage class system (group / plan / etc.). */
export const COVERAGE_CLASS_SYSTEM = 'http://terminology.hl7.org/CodeSystem/coverage-class';
/** HL7 v2-0203 identifier type system (used for the `MB` member-number identifier). */
export const IDENTIFIER_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v2-0203';
/** Identifier type code for an insurance member number. */
export const COVERAGE_MEMBER_ID_CODE = 'MB';
/** FHIR R4 subscriber-to-beneficiary relationship system. */
export const SUBSCRIBER_RELATIONSHIP_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/subscriber-relationship';

/** Extension: canonical legal entity name on Organization. */
export const PRACTICE_LEGAL_NAME_EXT = 'https://unify.health/fhir/ext/practice-legal-name';

/** Identifier system: EIN (Employer Identification Number). Unify-local; Claim submit also requires US_EIN_IDENTIFIER_SYSTEM. */
export const EIN_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/ein';
/** Identifier system: NPI (National Provider Identifier) — HL7 standard. */
export const NPI_IDENTIFIER_SYSTEM = 'http://hl7.org/fhir/sid/us-npi';
/** Code system: NUCC Health Care Provider Taxonomy — used on Practitioner.qualification[0] for claim rendering. */
export const NUCC_TAXONOMY_SYSTEM = 'http://nucc.org/provider-taxonomy';

// ═══════════════════════════════════════════════════════════════════════
//  Messaging Group / space extensions
// ═══════════════════════════════════════════════════════════════════════

export const MESSAGING_SPACE_RULES_EXT = 'https://unify.health/fhir/messaging/space-rules';
export const MESSAGING_SPACE_ROLE_EXT = 'https://unify.health/fhir/messaging/space-role';
export const MESSAGING_MANUAL_ADD_EXT = 'https://unify.health/fhir/messaging/manual-add';
export const MESSAGING_PRACTICE_TAG_SYSTEM = 'https://unify.health/fhir/practice-tag';

// ═══════════════════════════════════════════════════════════════════════
//  Bot identifiers
// ═══════════════════════════════════════════════════════════════════════

export const BOT_IDENTIFIER_SYSTEM = 'https://unify.health/bots';

// ═══════════════════════════════════════════════════════════════════════
//  Notification preferences extension
// ═══════════════════════════════════════════════════════════════════════

/** Per-user notification delivery preferences stored on Practitioner / Patient. */
export const NOTIFICATION_PREFERENCES_EXT = 'https://unify.health/fhir/notification-preferences';

// ═══════════════════════════════════════════════════════════════════════
//  Billing identifier systems
// ═══════════════════════════════════════════════════════════════════════

/** Root coding system for Unify billing codes and statuses. */
export const UNIFY_BILLING_SYSTEM = 'https://unify.health/fhir/billing';
/** Identifier system for Unify billing Account resources. */
export const UNIFY_BILLING_ACCOUNT_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/billing-account';
/** Identifier system for Unify Invoice resources. */
export const UNIFY_INVOICE_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/invoice';
/** Identifier system for Unify ChargeItem resources. */
export const UNIFY_CHARGE_ITEM_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/charge-item';
/** Identifier system for Unify payment records. */
export const UNIFY_PAYMENT_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/payment';
/** Identifier system for Unify patient statement documents. */
export const UNIFY_STATEMENT_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/statement';
/** Identifier system for Unify superbill documents. */
export const UNIFY_SUPERBILL_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/superbill';

// ═══════════════════════════════════════════════════════════════════════
//  Billing extension URLs
// ═══════════════════════════════════════════════════════════════════════

/** Extension: client (patient) dollar responsibility on an Invoice or ChargeItem (cents integer). */
export const BILLING_CLIENT_RESPONSIBILITY_EXTENSION =
  'https://unify.health/fhir/ext/billing-client-responsibility';
/** Extension: insurance dollar responsibility on an Invoice or ChargeItem (cents integer). */
export const BILLING_INSURANCE_RESPONSIBILITY_EXTENSION =
  'https://unify.health/fhir/ext/billing-insurance-responsibility';
/** Extension: amount the insurance payer actually paid (cents integer). */
export const BILLING_INSURANCE_PAID_EXTENSION =
  'https://unify.health/fhir/ext/billing-insurance-paid';
/** Extension: amount the client has paid (cents integer). */
export const BILLING_CLIENT_PAID_EXTENSION =
  'https://unify.health/fhir/ext/billing-client-paid';
/** Extension: write-off amount applied by the practice (cents integer). */
export const BILLING_WRITE_OFF_EXTENSION =
  'https://unify.health/fhir/ext/billing-write-off';
/** Extension: adjustment amount applied (cents integer, signed). */
export const BILLING_ADJUSTMENT_EXTENSION =
  'https://unify.health/fhir/ext/billing-adjustment';
/** Extension: Unify payment status code (see BILLING_PAYMENT_STATUS_CODES). */
export const BILLING_PAYMENT_STATUS_EXTENSION =
  'https://unify.health/fhir/ext/billing-payment-status';
/** Extension: payment method label (e.g. "Visa ending in 4242"). */
export const BILLING_PAYMENT_METHOD_EXTENSION =
  'https://unify.health/fhir/ext/billing-payment-method';
/** Extension: how the client payment was collected (`auto-charge` \| `manual`). */
export const BILLING_PAYMENT_CHARGE_TYPE_EXTENSION =
  'https://unify.health/fhir/ext/billing-payment-charge-type';
/** Extension: whether autopay is enabled for the billing account (boolean). */
export const BILLING_AUTOPAY_ENABLED_EXTENSION =
  'https://unify.health/fhir/ext/billing-autopay-enabled';
/** Extension: ISO date the invoice is due. */
export const BILLING_DUE_DATE_EXTENSION =
  'https://unify.health/fhir/ext/billing-due-date';
/** Extension: service period for an invoice or document (nested start/end). */
export const BILLING_SERVICE_PERIOD_EXTENSION =
  'https://unify.health/fhir/ext/billing-service-period';
/** Extension: billing document kind code (statement, superbill, eob, gfe, receipt, invoice-pdf). */
export const BILLING_DOCUMENT_KIND_EXTENSION =
  'https://unify.health/fhir/ext/billing-document-kind';
/** Extension: Stripe customer ID (internal; not displayed to client). */
export const BILLING_EXTERNAL_STRIPE_CUSTOMER_ID_EXTENSION =
  'https://unify.health/fhir/ext/billing-stripe-customer-id';
/** Extension: Stripe payment method ID (internal; not displayed to client). */
export const BILLING_EXTERNAL_STRIPE_PAYMENT_METHOD_ID_EXTENSION =
  'https://unify.health/fhir/ext/billing-stripe-payment-method-id';
/** Extension: Candid claim ID (internal; not displayed to client). */
export const BILLING_EXTERNAL_CANDID_CLAIM_ID_EXTENSION =
  'https://unify.health/fhir/ext/billing-candid-claim-id';

// ═══════════════════════════════════════════════════════════════════════
//  Billing document kind codes
// ═══════════════════════════════════════════════════════════════════════

export const BILLING_DOCUMENT_KIND_CODES = [
  'statement',
  'superbill',
  'eob',
  'gfe',
  'receipt',
  'invoice-pdf',
  'insurance-card-front',
  'insurance-card-back',
] as const;
export const BILLING_DOCUMENT_KIND_SYSTEM =
  'https://unify.health/fhir/CodeSystem/billing-document-kind';

/** Extension: insurance type on Coverage (commercial, medicare, medicaid). */
export const COVERAGE_INSURANCE_TYPE_EXT =
  'https://unify.health/fhir/ext/coverage-insurance-type';

export const COVERAGE_INSURANCE_TYPE_CODES = [
  'commercial',
  'medicare',
  'medicaid',
] as const;

export type CoverageInsuranceTypeCode = (typeof COVERAGE_INSURANCE_TYPE_CODES)[number];

/** CodeSystem for insurance authorization Basic resources. */
export const INSURANCE_AUTHORIZATION_CODE_SYSTEM =
  'https://unify.health/fhir/cs/insurance-authorization';

export const INSURANCE_AUTHORIZATION_CODE = 'insurance-authorization';

export const AUTH_COVERAGE_REF_EXT =
  'https://unify.health/fhir/ext/auth-coverage-ref';
export const AUTH_NUMBER_EXT =
  'https://unify.health/fhir/ext/auth-number';
export const AUTH_VALID_FROM_EXT =
  'https://unify.health/fhir/ext/auth-valid-from';
export const AUTH_VALID_THROUGH_EXT =
  'https://unify.health/fhir/ext/auth-valid-through';
export const AUTH_LIMIT_TYPE_EXT =
  'https://unify.health/fhir/ext/auth-limit-type';
export const AUTH_AUTHORIZED_USES_EXT =
  'https://unify.health/fhir/ext/auth-authorized-uses';
export const AUTH_USED_COUNT_EXT =
  'https://unify.health/fhir/ext/auth-used-count';
export const AUTH_SERVICE_TYPE_EXT =
  'https://unify.health/fhir/ext/auth-service-type';
export const AUTH_NOTES_EXT =
  'https://unify.health/fhir/ext/auth-notes';
export const AUTH_REMINDER_THRESHOLD_EXT =
  'https://unify.health/fhir/ext/auth-reminder-threshold';
export const AUTH_REMIND_MONTH_BEFORE_EXPIRY_EXT =
  'https://unify.health/fhir/ext/auth-remind-month-before-expiry';
export const AUTH_MANUAL_INACTIVE_EXT =
  'https://unify.health/fhir/ext/auth-manual-inactive';

export const AUTH_LIMIT_TYPE_CODES = ['unlimited', 'limited', 'date-range'] as const;
export type AuthLimitTypeCode = (typeof AUTH_LIMIT_TYPE_CODES)[number];

export const AUTH_STATUS_CODES = [
  'active',
  'pending',
  'expired',
  'exhausted',
  'cancelled',
] as const;
export type AuthStatusCode = (typeof AUTH_STATUS_CODES)[number];

export type BillingDocumentKindCode = (typeof BILLING_DOCUMENT_KIND_CODES)[number];

// ═══════════════════════════════════════════════════════════════════════
//  Billing payment/invoice status codes
// ═══════════════════════════════════════════════════════════════════════

export const BILLING_PAYMENT_STATUS_CODES = [
  'due',
  'overdue',
  'paid',
  'partially-paid',
  'processing',
  'void',
  'refunded',
  'insurance-pending',
  'insurance-paid',
  'client-responsibility-pending',
] as const;

export type BillingPaymentStatusCode = (typeof BILLING_PAYMENT_STATUS_CODES)[number];

export const BILLING_PAYMENT_CHARGE_TYPE_CODES = ['auto-charge', 'manual'] as const;
export type BillingPaymentChargeTypeCode = (typeof BILLING_PAYMENT_CHARGE_TYPE_CODES)[number];

// ═══════════════════════════════════════════════════════════════════════
//  CPT / HCPCS coding system
// ═══════════════════════════════════════════════════════════════════════

/** Standard AMA CPT coding system URI. */
export const CPT_CODING_SYSTEM = 'http://www.ama-assn.org/go/cpt';

// ═══════════════════════════════════════════════════════════════════════
//  ROI (Release of Information) Consent constants
// ═══════════════════════════════════════════════════════════════════════

/** CodeSystem for Unify Consent category codes. */
export const CONSENT_CATEGORY_SYSTEM = 'https://unify.health/fhir/cs/consent-category';
/** Category code identifying a Consent as a Release of Information record. */
export const ROI_CONSENT_CATEGORY_CODE = 'roi';

/** Category code identifying Consent for recording telehealth sessions for documentation. */
export const SESSION_RECORDING_CONSENT_CATEGORY_CODE = 'session-recording';

/** Canonical Questionnaire URL for session-recording consent intake / re-opt-in forms. */
export const SESSION_RECORDING_CONSENT_QUESTIONNAIRE_URL =
  'https://unify.health/fhir/Questionnaire/meridian-counseling-session-recording-consent';

/** Questionnaire / template id matching {@link SESSION_RECORDING_CONSENT_QUESTIONNAIRE_URL}. */
export const SESSION_RECORDING_CONSENT_QUESTIONNAIRE_ID = 'meridian-counseling-session-recording-consent';

/** Stable linkIds on {@link SESSION_RECORDING_CONSENT_QUESTIONNAIRE_URL}. */
export const SESSION_RECORDING_CONSENT_LINK_IDS = {
  decision: 'session-recording-decision',
} as const;

/** Answer Coding system for {@link SESSION_RECORDING_CONSENT_LINK_IDS.decision}. */
export const SESSION_RECORDING_DECISION_ANSWER_SYSTEM =
  'https://unify.health/fhir/cs/session-recording-decision';

/** Category code for client consent to AI documentation tools. */
export const AI_TOOLS_CONSENT_CATEGORY_CODE = 'ai-tools';

/** Canonical Questionnaire URL for the AI tools consent form. */
export const AI_TOOLS_CONSENT_QUESTIONNAIRE_URL =
  'https://unify.health/fhir/Questionnaire/unify-ai-tools-consent';

/** Questionnaire / template id matching {@link AI_TOOLS_CONSENT_QUESTIONNAIRE_URL}. */
export const AI_TOOLS_CONSENT_QUESTIONNAIRE_ID = 'unify-ai-tools-consent';

/** Stable linkIds on {@link AI_TOOLS_CONSENT_QUESTIONNAIRE_URL}. */
export const AI_TOOLS_CONSENT_LINK_IDS = {
  transcription: 'ai-transcription-decision',
} as const;

/** Client signature JSON stored on a portal QuestionnaireResponse. */
export const CLIENT_SIGNATURE_EXT_URL =
  'https://unify.health/fhir/StructureDefinition/client-signature';

/** CodeSystem for ROI information-release purpose codes (maps to ROI_PERMISSION_LABELS keys). */
export const ROI_CONSENT_PURPOSE_SYSTEM = 'https://unify.health/fhir/cs/roi-purpose';

/** HL7 consent scope — patient-privacy (standard FHIR R4). */
export const CONSENT_SCOPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/consentscope';

/** HL7 extra-security-role-type for the ROI recipient actor role (Information Recipient). */
export const CONSENT_ACTOR_ROLE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/extra-security-role-type';
/** Actor role code: Information Recipient (used on Consent.provision.actor). */
export const CONSENT_ACTOR_IRCP_CODE = 'IRCP';

/**
 * Extension on Consent: display name of the Patient.contact entry when the
 * authorized contact is embedded in Patient.contact[] (not a standalone RelatedPerson).
 */
export const ROI_PATIENT_CONTACT_NAME_EXT = 'https://unify.health/fhir/roi-patient-contact-name';

/** Extension on Consent: free-text "other" description when the 'other' purpose is selected. */
export const ROI_CONSENT_OTHER_DESCRIPTION_EXT = 'https://unify.health/fhir/roi-other-description';

/** Canonical Questionnaire URL for client-signed contact ROI authorization forms. */
export const COORDINATION_OF_CARE_ROI_QUESTIONNAIRE_URL =
  'https://unify.health/fhir/Questionnaire/meridian-counseling-release-of-information-coordination-of-care';

/** @deprecated Use {@link COORDINATION_OF_CARE_ROI_QUESTIONNAIRE_URL}. */
export const CONTACT_ROI_QUESTIONNAIRE_URL = COORDINATION_OF_CARE_ROI_QUESTIONNAIRE_URL;

/** QuestionnaireResponse extension linking a pending ROI form to its draft Consent. */
export const ROI_CONSENT_REF_EXT = 'https://unify.health/fhir/roi-consent-ref';

/** Stable linkIds on {@link COORDINATION_OF_CARE_ROI_QUESTIONNAIRE_URL}. */
export const COORDINATION_OF_CARE_ROI_LINK_IDS = {
  clientName: 'meridian-counseling-release-of-information-coordination-of-care-q001',
  authorizeDirection: 'meridian-counseling-release-of-information-coordination-of-care-q002',
  informationCategories: 'meridian-counseling-release-of-information-coordination-of-care-q003',
  informationOtherAddon: 'meridian-counseling-release-of-information-coordination-of-care-q003-opt-id-675-addon',
  toFromContact: 'meridian-counseling-release-of-information-coordination-of-care-q004',
  toFromOtherName: 'meridian-counseling-release-of-information-coordination-of-care-q004-other-name',
  toFromOtherPhone: 'meridian-counseling-release-of-information-coordination-of-care-q004-other-phone',
  toFromOtherRelationship: 'meridian-counseling-release-of-information-coordination-of-care-q004-other-relationship',
  phone: 'meridian-counseling-release-of-information-coordination-of-care-q005',
  fax: 'meridian-counseling-release-of-information-coordination-of-care-q006',
  signerRelationship: 'meridian-counseling-release-of-information-coordination-of-care-q007',
  purposes: 'meridian-counseling-release-of-information-coordination-of-care-q008',
  signatureDate: 'meridian-counseling-release-of-information-coordination-of-care-q011',
  witnessSignature: 'meridian-counseling-release-of-information-coordination-of-care-q012',
  witnessDate: 'meridian-counseling-release-of-information-coordination-of-care-q013',
} as const;

/** @deprecated Use {@link COORDINATION_OF_CARE_ROI_LINK_IDS}. */
export const CONTACT_ROI_LINK_IDS = COORDINATION_OF_CARE_ROI_LINK_IDS;

/** Template identifier system on seeded Unify Questionnaire resources. */
export const QUESTIONNAIRE_TEMPLATE_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/questionnaire-template';

/** Canonical Questionnaire URL for minor custody & consent screening intake. */
export const MINOR_CUSTODY_CONSENT_SCREENING_QUESTIONNAIRE_URL =
  'https://unify.health/fhir/Questionnaire/unify-minor-custody-consent-screening';

export const MINOR_CUSTODY_CONSENT_SCREENING_TEMPLATE_ID = 'unify-minor-custody-consent-screening';

/** Stable linkIds on {@link MINOR_CUSTODY_CONSENT_SCREENING_QUESTIONNAIRE_URL}. */
export const MINOR_CUSTODY_CONSENT_SCREENING_LINK_IDS = {
  section: 'unify-minor-custody-consent-screening-section-01',
  custodyAgreement: 'unify-minor-custody-consent-screening-q001',
  custodyUpload: 'unify-minor-custody-consent-screening-q002',
  guardianName: 'unify-minor-custody-consent-screening-q003',
  relationship: 'unify-minor-custody-consent-screening-q004',
  relationshipOther: 'unify-minor-custody-consent-screening-q004-other',
  guardianPhone: 'unify-minor-custody-consent-screening-q005',
  guardianEmail: 'unify-minor-custody-consent-screening-q006',
  authorityConfirmed: 'unify-minor-custody-consent-screening-q007',
} as const;

export const GROUP_THERAPY_SCREENING_NOTE_TEMPLATE_ID = UNIFY_NOTE_TEMPLATE_IDS.screening;

/** Canonical Questionnaire URL for Group Therapy / general screening notes. */
export const GROUP_THERAPY_SCREENING_NOTE_QUESTIONNAIRE_URL =
  `${QUESTIONNAIRE_CANONICAL_URL_PREFIX}${GROUP_THERAPY_SCREENING_NOTE_TEMPLATE_ID}`;

/** Stable linkIds on {@link GROUP_THERAPY_SCREENING_NOTE_QUESTIONNAIRE_URL}. */
export const GROUP_THERAPY_SCREENING_NOTE_LINK_IDS = {
  types: 'unify-screening-note-types',
  groupOutcome: 'unify-screening-note-group-outcome',
  groupNotes: 'unify-screening-note-group-notes',
  medicationManagementNotes: 'unify-screening-note-medication-management-notes',
  highRiskNotes: 'unify-screening-note-high-risk-notes',
  overallOutcome: 'unify-screening-note-overall-outcome',
  /** Legacy group-only screening note (read fallback). */
  notes: 'group-therapy-screening-notes',
  result: 'group-therapy-screening-result',
} as const;

export const SCREENING_TYPE_CODES = {
  groupTherapy: 'group-therapy',
  medicationManagement: 'medication-management',
  highRisk: 'high-risk',
} as const;

// ═══════════════════════════════════════════════════════════════════════
//  Migration ledger (scripts/migrations — see docs/prd/medplum/PRD_MEDPLUM_MIGRATIONS.md)
// ═══════════════════════════════════════════════════════════════════════

/** Identifier system on the per-migration `Basic` ledger records. */
export const MIGRATION_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/migration';
export const MIGRATION_LEDGER_CS = 'https://unify.health/fhir/CodeSystem/migration-ledger';
export const APPLIED_MIGRATION_CODE = 'applied-migration';
export const MIGRATION_APPLIED_AT_EXT = 'https://unify.health/fhir/StructureDefinition/migration-applied-at';
export const MIGRATION_DURATION_MS_EXT = 'https://unify.health/fhir/StructureDefinition/migration-duration-ms';
/** valueCode: 'applied' (runner executed it) | 'faked' (recorded via --fake). */
export const MIGRATION_MODE_EXT = 'https://unify.health/fhir/StructureDefinition/migration-mode';

// ═══════════════════════════════════════════════════════════════════════
//  SimplePractice historical chart migration (scripts/migrate-sp-history)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Idempotency key for historical chart DocumentReferences / treatment-plan QRs.
 * Value examples: `upload/{slug}`, `progress-note/{id}`, `psychotherapy-upload/{patientId}/{ts}`.
 */
export const HISTORICAL_RECORD_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/historical-record';

/**
 * Legacy idempotency key used only by BigQuery `migrate-sp-history` commits
 * (`{recordType}/{spRecordId}`). UI folder uploads must use
 * {@link HISTORICAL_RECORD_IDENTIFIER_SYSTEM} — never stamp SimplePractice branding
 * on chart resources.
 */
export const SIMPLEPRACTICE_RECORD_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/identifier/simplepractice-record';

/** meta.tag on historical imports (staff chart DocumentReferences / TP QRs). */
export const HISTORICAL_MIGRATION_TAG_SYSTEM = 'https://unify.health/fhir/CodeSystem/historical-migration';
export const HISTORICAL_MIGRATION_TAG_CODES = {
  historical: 'historical',
} as const;

/** DOCUMENT_TYPE_SYSTEM codes for immutable SP historical PDF DocumentReferences. */
export const SP_HISTORICAL_DOCUMENT_TYPE_CODES = {
  progressNote: 'historical-progress-note',
  psychotherapyNote: 'historical-psychotherapy-note',
  intakeNote: 'historical-intake-note',
  mentalStatusExam: 'historical-mental-status-exam',
  chartNote: 'historical-chart-note',
  assessmentNote: 'historical-assessment-note',
  treatmentPlan: 'historical-treatment-plan',
  form: 'historical-form',
  uploadedDocument: 'historical-document',
} as const;

/**
 * SP `sp_notes.type` values skipped by the regular chart-note inventory.
 * Psychotherapy notes are imported separately as one private PDF bundle.
 * DiagnosisTreatmentPlan notes are skipped — real plans come from `sp_treatment_plans`.
 */
export const SP_NOTE_TYPES_EXCLUDED = [
  'Waitlist',
  'Psychotherapy',
  'DiagnosisTreatmentPlan',
] as const;

/**
 * Maps SP note `thisType` → historical DocumentReference type code.
 * Excluded types are listed in SP_NOTE_TYPES_EXCLUDED (and Psychotherapy is a
 * separate private-bundle path).
 */
export const SP_NOTE_TYPE_TO_HISTORICAL_DOC_TYPE: Record<string, string> = {
  Progress: SP_HISTORICAL_DOCUMENT_TYPE_CODES.progressNote,
  IntakeNote: SP_HISTORICAL_DOCUMENT_TYPE_CODES.intakeNote,
  MentalStatusExam: SP_HISTORICAL_DOCUMENT_TYPE_CODES.mentalStatusExam,
  Chart: SP_HISTORICAL_DOCUMENT_TYPE_CODES.chartNote,
  Assessment: SP_HISTORICAL_DOCUMENT_TYPE_CODES.assessmentNote,
};
